import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Typography, Tag, Select, Space, Button, Modal, Input, Textarea, InputNumber, Popconfirm, DatePicker, Descriptions, FilterResetButton, Tooltip, Spinner, EmptyState, Tabs, Table, message, StatCard, statTone, Avatar, SectionHead, SECTION_TINT } from '../../ui';
import type { TagTone } from '../../ui';
import { Plus, Eye, Pencil, Trash2, Search, MapPin, Download, Check, Wallet, HandCoins, CheckCircle2, ShieldQuestion, ArrowDownLeft, ArrowUpRight, Undo2, Landmark, Users, Banknote, ArrowRight, History, FileText, Info, Phone } from 'lucide-react';
import { UserLink } from '../../components/EntityLinks';
import { RoleBadge } from '../../components/RoleBadge';
import { useFilterPanel, FilterRange, type FilterChip } from '../../components/FilterPanel';
import { useServerSort } from '../../hooks/useServerSort';
import { useFocusRow } from '../../hooks/useFocusRow';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlString, useUrlList, useUrlBool, useUrlRangeOrNull, useUrlDate } from '../../hooks/useUrlState';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { MyMoneyLedger } from './MyMoneyLedger';
import { ExportDialog } from '../../components/ExportDialog';
import { disableFuture } from '../../lib/dateRange';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { usePermission, useHasRole, useAuthStore } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useUrlTab } from '../../hooks/useUrlTab';
import { useBadges } from '../../hooks/useBadges';
import { formatIst, formatIstDate } from '../../lib/time';
import { FileUploadField } from '../../components/FileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { CopyValue } from '../../components/CopyValue';
import { AdvanceRequestsPage, RequestDetailModal } from './AdvanceRequestsPage';
import { inr2 } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { bankAcctOption } from '../billing/PaymentFormModal';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;

// Expense-report status chip colours (mirrors the Expense Reports page).
const REPORT_STATUS_COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', SUBMITTED: 'gold', APPROVED: 'cyan', CLOSED: 'green', CORRECTION_REQUIRED: 'gold', REJECTED: 'red',
};

// Advance / top-up request status chip colours.
const ADV_STATUS_COLOR: Record<string, TagTone> = {
  REQUESTED: 'gold', APPROVED: 'blue', DISBURSED: 'green', FUNDED_FROM_CASH: 'gold', REJECTED: 'red',
};
/** The same plain wording the requests register uses — one vocabulary across both screens. */
const ADV_STATUS_LABEL: Record<string, string> = {
  REQUESTED: 'Awaiting approval', APPROVED: 'Awaiting payment', DISBURSED: 'Paid',
  /* Approved, but nothing was paid out — collected cash they already held was moved into their wallet.
     "Approved" alone would leave them waiting for a payment that is never coming. */
  FUNDED_FROM_CASH: 'From collection cash — nothing paid out',
  REJECTED: 'Rejected',
};

const ROLE_TAG: Record<string, { label: string; tone: TagTone }> = {
  SUPER_ADMIN: { label: 'Super Admin', tone: 'red' },
  ADMIN: { label: 'Admin', tone: 'red' },
  ACCOUNTS: { label: 'Accounts', tone: 'gold' },
  SALES_HEAD: { label: 'Sales Head', tone: 'purple' },
  SALES_PERSON: { label: 'Sales Person', tone: 'blue' },
};
const RoleTag = ({ role, name }: { role?: string; name?: string | null }) => {
  const meta = role ? ROLE_TAG[role] : undefined;
  if (!meta) return null;
  // Pass `name` where the person's name is shown right next to the capsule: an account literally
  // called "Super Admin" would otherwise render "Super Admin [Super Admin]".
  const norm = (s?: string | null) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (name && norm(name) === norm(meta.label)) return null;
  return <Tag tone={meta.tone}>{meta.label}</Tag>;
};

// Wallet balance semantics: a positive balance is company float still sitting with the
// rep (outstanding), a negative balance means the rep is owed a reimbursement, zero is settled.
/** Fallback for a rep with no photo on file — their initials, not a generic silhouette. */
function initials(name?: string | null): string {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function balanceColor(v: any): string {
  const n = Number(v ?? 0);
  if (n > 0) return PALETTE.brand[600];
  if (n < 0) return PALETTE.danger[500];
  return '#8c8c8c';
}
function balanceLabel(v: any): string {
  const n = Number(v ?? 0);
  if (n > 0) return 'float outstanding';
  if (n < 0) return 'reimbursement due';
  return 'settled';
}
/**
 * Money direction → text colour CLASSES, for the figures big enough that dark mode matters.
 *
 * `balanceColor()` above returns a raw hex and stays as-is for inline/table use, but its brand-600 /
 * danger-500 are tuned against a WHITE surface: at 24px on the dark theme's near-black card they sit
 * around 3:1 and read as dimmed rather than coloured. Each direction therefore carries a lighter
 * dark-mode partner, the same pairing the ui kit uses elsewhere.
 */
const IN_TEXT = 'text-brand-700 dark:text-brand-400';
const OUT_TEXT = 'text-danger-600 dark:text-danger-500';
function balanceTextCls(v: any): string {
  const n = Number(v ?? 0);
  if (n > 0) return IN_TEXT;
  if (n < 0) return OUT_TEXT;
  return 'text-muted';
}

// Balance sign → Tag tone (green = float outstanding, red = reimbursement due, neutral = settled).
function balanceTone(v: any): TagTone {
  const c = balanceColor(v);
  return c === PALETTE.brand[600] ? 'green' : c === PALETTE.danger[500] ? 'red' : 'neutral';
}

/**
 * A money cell. Deliberately NOT bold by default: the sign and the colour already carry the
 * meaning, so bolding every figure just flattens the page — when everything is emphasised,
 * nothing is. `strong` (medium, not bold) is reserved for the ONE value that is the point of
 * its row: the ledger amount, or a rep's current balance. `tabular-nums` keeps the column
 * aligned as digits change width.
 */
const Money = ({ value, color, strong, strike }: { value: string; color?: string; strong?: boolean; strike?: boolean }) => (
  <span
    className={`tabular-nums${strong ? ' font-medium' : ''}${strike ? ' line-through' : ''}`}
    style={color ? { color } : undefined}
  >
    {value}
  </span>
);

/**
 * KPI row: a FIXED four-column rhythm (two on small screens), the same one the other KPI pages use
 * (Dispatches, Credit notes, Inventory dashboard, and the antd-grid `xs={12} md={6}` pages).
 *
 * The tiles used to be flex children that grew to fill the row, so their width was set by how many
 * there happened to be — four tiles read fine, but the two on "Advances given" each stretched to half
 * a 1900px screen for a five-character number. A KPI is a glanceable figure, not a banner: it should
 * be the SAME size on every tab, and a row with two of them simply ends after the second one.
 */
const KPI_ROW = 'mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4';

const TXN_TYPES = ['ADVANCE_IN', 'REIMBURSEMENT', 'RETURN', 'ADJUSTMENT', 'OPENING_BALANCE'] as const;
const TXN_TYPE_COLOR: Record<string, TagTone> = {
  ADVANCE_IN: 'blue', REIMBURSEMENT: 'cyan', RETURN: 'gold', ADJUSTMENT: 'purple', OPENING_BALANCE: 'blue',
};
// Plain-language names for the ledger types. The stored values are SCREAMING_SNAKE constants;
// nobody reading their own cash ledger should have to parse "EXPENSE_OUT".
const TYPE_LABEL: Record<string, string> = {
  ADVANCE_IN: 'Advance given', REIMBURSEMENT: 'Reimbursement', RETURN: 'Returned by rep',
  ADJUSTMENT: 'Adjustment', OPENING_BALANCE: 'Opening balance',
  EXPENSE_OUT: 'Expense', EXPENSE_REVERSAL: 'Expense reversed',
  REP_CASH_IN: 'Cash received', REP_CASH_OUT: 'Cash spent',
  COLLECTION_TO_FLOAT: 'From collection cash',
};
const typeLabel = (t: string) => TYPE_LABEL[t] ?? String(t ?? '').replace(/_/g, ' ').toLowerCase();
const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'];
const MODE_LABEL: Record<string, string> = {
  CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank transfer', CHEQUE: 'Cheque', CARD: 'Card', OTHER: 'Other',
};
const modeLabel = (m?: string | null) => (m ? MODE_LABEL[m] ?? String(m).replace(/_/g, ' ') : '');
const VERIFY_COLOR: Record<string, TagTone> = {
  NOT_REQUIRED: 'neutral', UNVERIFIED: 'gold', VERIFIED: 'green',
};
// Plain-language names for the ledger's verification filter — the stored values are constants, and
// "UNVERIFIED" is not what a reader would call a row nobody has checked yet.
const VERIFY_FILTER_LABEL: Record<string, string> = {
  UNVERIFIED: 'Not checked', VERIFIED: 'Verified', NOT_REQUIRED: 'No check needed',
};
// Mirrors the server's cap on one ledger read. Hitting it means the answer is a WINDOW, not the whole
// history, so the table says so and points at the export rather than letting a total quietly mislead.
const LEDGER_CAP = 500;

/** One line of an export dialog's "What this file will contain" block. */
function ScopeRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 pt-px type-label text-subtle">{label}</span>
      <span className="min-w-0 flex-1 type-body-medium text-content">{children}</span>
    </div>
  );
}

export function WalletsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canApproveHead = usePermission('EXPENSE.APPROVE.HEAD');
  const canApproveAcct = usePermission('EXPENSE.APPROVE.ACCT');
  // The company-wide custody view is gated exactly like the endpoint behind it.
  const canViewCustody = usePermission('CASH.CUSTODY.VIEW');
  // Only real FIELD staff carry a wallet — a Sales Person or a Sales Head, who punch in and
  // visit. A Sales Head gets BOTH views: the team's wallets and their own float. This must be a
  // ROLE test, not a permission one: SUPER_ADMIN holds '*', so `usePermission('ADVANCE.REQUEST')`
  // is true for them and would offer an admin a "My wallet" / "Request top-up" they can't use
  // (the backend rejects it with REQUEST_ROLE_ONLY).
  const isFieldStaff = useHasRole('SALES_PERSON', 'SALES_HEAD');
  // Declared before the early returns below — hooks can't run conditionally. In the URL so Back from a
  // staff/expense page returns to the tab you were on.
  // `collections` = the company-wide custody view. An unlisted key silently degrades to the default,
  // so a tab missing from here renders the WRONG panel rather than failing loudly.
  const [tab, setTab] = useUrlTab('tab', ['team', 'collections', 'mine', 'retailer'] as const, 'team');
  /*
   * Does THIS person actually hold collected cash?
   *
   * Anyone allowed to take cash from a retailer can end up holding some — Accounts taking a payment at
   * the counter is the everyday case, and ₹24,978 was sitting with three non-field people with no way
   * to bank it. But an Admin who has never touched cash does not want a personal money tab on what is,
   * for them, an oversight page. So the tab follows the MONEY, not the job title: it appears for field
   * staff always, and for anybody else only while they are actually carrying something.
   */
  const myCollection = useQuery({
    queryKey: ['retailer-money', 'me'],
    queryFn: () => request<{ outstanding: number }>({ url: '/retailer-money/me' }),
    retry: false,
  });
  const holdsCollectionMoney = Number(myCollection.data?.outstanding ?? 0) > 0.005;

  const isFleet =
    canApproveHead || canApproveAcct ||
    role === 'SALES_HEAD' || role === 'ADMIN' || role === 'ACCOUNTS' || role === 'SUPER_ADMIN';

  /*
   * Built as a LIST rather than three early returns, because "collection money" belongs to everyone
   * who can take cash from a retailer — not only to fleet managers. The old shape returned a bare
   * MyWalletView for a plain sales person, so the one tab a rep most needs was the one tab a rep
   * could never reach.
   *
   * Collection money is NOT a wallet, so it gets its own tab rather than a card stacked under the
   * float: the single mistake this model exists to prevent is reading the two as one balance, and
   * nothing invites that more than printing them on the same screen.
   */
  const items = [
    ...(isFleet ? [{ key: 'team', label: 'Team wallets', children: <FleetView canRecord={canApproveAcct} /> }] : []),
    /*
     * THE COMPANY'S COLLECTION MONEY — the view that was missing.
     *
     * A Super Admin opening "Collection money" got their OWN ₹200 and no hint that the label meant
     * "yours": six people were holding ₹28,513 between them, and the one number on screen was the
     * smallest of the six. Reading it as the company position is not a mistake a reader can be blamed
     * for — nothing on the tab said otherwise, and no company view existed to compare it against.
     * `/retailer-money/holders` (CASH.CUSTODY.VIEW) has always returned exactly this; it simply had no
     * screen. The personal tab below is now explicitly "Mine".
     */
    ...(canViewCustody ? [{ key: 'collections', label: 'Collection money', children: <CollectionMoneyOversight /> }] : []),
    ...(isFieldStaff ? [{ key: 'mine', label: 'My wallet', children: <MyWalletView /> }] : []),
    /* Field staff always; anyone else only while they are holding something — see above. */
    ...(isFieldStaff || holdsCollectionMoney
      ? [{
        // "My" is not decoration here: it is the word that stops one person's figure being read as
        // everybody's.
        key: 'retailer',
        label: canViewCustody ? 'My collection money' : 'Collection money',
        children: <MyMoneyLedger only="RETAILER_MONEY" />,
      }]
      : []),
  ];
  // One tab is not a tab bar — it is just the page.
  if (items.length === 1) return <>{items[0].children}</>;
  return (
    <Tabs
      activeKey={items.some((i) => i.key === tab) ? tab : items[0].key}
      onChange={setTab}
      items={items}
    />
  );
}

// Straight-line (haversine) distance in km — the always-available fallback for road
// distance/ETA (an OSRM /table call could replace this when an OSRM endpoint is configured).
/**
 * EVERY PERSON HOLDING COMPANY CASH, AND HOW MUCH.
 *
 * The company's side of collection money. It answers the question the personal tab cannot — "how much
 * of our cash is out there, and with whom?" — from `/retailer-money/holders`, which already computed
 * it: collected minus what has reached a bank, per custodian, with the age of the oldest uncleared
 * collection.
 *
 * It is deliberately NOT a wallet view. A wallet float is money the company released to somebody to
 * spend; this is money that belongs to the company and is sitting in somebody's pocket. The two are
 * never added, which is why they are separate tabs rather than columns of one table.
 */
function CollectionMoneyOversight() {
  const { data, isLoading } = useQuery({
    queryKey: ['retailer-money', 'holders'],
    queryFn: () => request<any>({ url: '/retailer-money/holders' }),
  });
  const navigate = useNavigate();
  const holders: any[] = data?.holders ?? [];
  const t = data?.totals ?? {};
  const unallocated: any[] = data?.unallocatedDeposits ?? [];
  const myId = useAuthStore((s) => s.user?.id);
  /** Which slice of the holders the tiles are showing. One value, so a click always REPLACES. */
  const [view, setView] = useState<'all' | 'holding' | 'banked'>('all');
  const [showUnallocated, setShowUnallocated] = useState(false);
  /** Which person's out-leg is being opened, and which of the two it is. */
  const [spendFor, setSpendFor] = useState<{ row: any; leg: 'CLAIM' | 'WALLET' } | null>(null);
  const holding = holders.filter((h) => Number(h.outstanding) > 0.005);
  const banked = holders.filter((h) => Number(h.deposited) > 0.005);
  const shown = view === 'holding' ? holding : view === 'banked' ? banked : holders;

  const columns: DataTableColumn<any>[] = [
    {
      key: 'staffName', title: 'Who is holding it', dataIndex: 'staffName', alwaysVisible: true, width: 260,
      render: (v: string, r: any) => (
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Their actual face where they have one; initials otherwise — same treatment the team
              wallets list beside it already gives a person. */}
          <Avatar size={32} src={r.staffPhotoUrl ?? undefined} fallback={initials(v)} />
          <div className="min-w-0">
            {/* Role as a coloured pill BESIDE the name, the way every other person is drawn in this
                app — this list mixes a Super Admin, an Accountant, a Sales Head and two reps, and a
                grey lowercase line underneath made them all look alike. */}
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <UserLink id={r.staffId} className="truncate">{v}</UserLink>
              <RoleBadge role={r.staffRole} />
              {String(r.staffId) === String(myId) && <Tag tone="green">you</Tag>}
            </div>
            <div className="truncate type-caption text-muted">
              {r.lastCollectedOn ? `last collected ${formatIstDate(r.lastCollectedOn)}` : 'no collections yet'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'outstanding', title: 'Still with them', dataIndex: 'outstanding', align: 'right', width: 170, sortable: true,
      render: (v: any, r: any) => (
        <div>
          <b className={Number(v) > 0.005 ? 'tabular-nums text-warning-600 dark:text-harvest-300' : 'tabular-nums text-subtle'}>{inr2(v)}</b>
          {Number(r.awaitingConfirmation) > 0.005 && (
            <div className="type-caption text-subtle">{inr2(r.awaitingConfirmation)} banked, awaiting Accounts</div>
          )}
        </div>
      ),
    },
    {
      key: 'collected', title: 'Collected', dataIndex: 'collected', align: 'right', width: 150, sortable: true,
      render: (v: any, r: any) => (
        <div>
          <span className="tabular-nums">{inr2(v)}</span>
          <div className="type-caption text-subtle">{r.payments} payment{r.payments === 1 ? '' : 's'}</div>
        </div>
      ),
    },
    {
      key: 'deposited', title: 'Reached the company', dataIndex: 'deposited', align: 'right', width: 170, sortable: true,
      render: (v: any) => (Number(v) > 0.005
        ? <span className="tabular-nums text-brand-700 dark:text-brand-300">{inr2(v)}</span>
        : <span className="type-caption text-subtle">nothing yet</span>),
    },
    {
      /*
       * "Spent from it" said nothing — spent on what, by whom, and does it still have to come back?
       *
       * It is approved expense claims this person paid for out of the very cash they were holding
       * (a claim whose funding source is CASH_CUSTODY). That money never reaches a bank and is not
       * owed back, so the server subtracts it alongside deposits:
       *   collected − reached the company − spent on claims = still with them.
       * Leaving it unnamed made the third of those terms invisible and the subtraction unfollowable.
       */
      key: 'spentOnExpenses', title: 'Spent on approved claims', dataIndex: 'spentOnExpenses', align: 'right', width: 190,
      /* Opens the claims it is made of. The row's own click goes to the person's page, which is the
         right answer for "who is this?" and the wrong one for "where did ₹2,434 go?" — that page does
         not carry this figure at all, so falling through to it answered a question nobody asked and
         left the number untraceable. Stop the row click and show the documents instead. */
      render: (v: any, r: any) => (Number(v) > 0.005
        ? (
          <Tooltip
            block
            label="Approved expense claims they paid for out of this cash. It never reaches a bank and is not owed back, so it counts towards clearing their balance. Click to see which claims."
            onClick={(e) => { e.stopPropagation(); setSpendFor({ row: r, leg: 'CLAIM' }); }}
          >
            {/* `block` so the trigger fills the cell: a right-aligned figure leaves most of a 190px
                column empty, and a click in that empty half would fall through to the row and open the
                person instead of the claims — the exact wrong turn this drill exists to fix. */}
            <span className="block w-full cursor-pointer text-right tabular-nums text-muted underline decoration-dotted underline-offset-2 hover:text-brand-700 dark:hover:text-brand-300">{inr2(v)}</span>
          </Tooltip>
        )
        : <span className="text-subtle">—</span>),
    },
    {
      /* The fourth term of the same subtraction: collected cash moved into their wallet for an approved advance. It is
         company float now, so it has left this book — but it was not spent, and it is not a claim. */
      key: 'movedToWallet', title: 'Moved into their wallet', dataIndex: 'movedToWallet', align: 'right', width: 180,
      render: (v: any, r: any) => (Number(v) > 0.005
        ? (
          <Tooltip
            block
            label="Collected cash turned into wallet float for an approved advance. It is now in their wallet, so it no longer counts here. Click to see which advances."
            onClick={(e) => { e.stopPropagation(); setSpendFor({ row: r, leg: 'WALLET' }); }}
          >
            <span className="block w-full cursor-pointer text-right tabular-nums text-muted underline decoration-dotted underline-offset-2 hover:text-brand-700 dark:hover:text-brand-300">{inr2(v)}</span>
          </Tooltip>
        )
        : <span className="text-subtle">—</span>),
    },
    {
      key: 'ageDays', title: 'Oldest uncleared', dataIndex: 'ageDays', align: 'right', width: 160, sortable: true,
      render: (v: any, r: any) => {
        if (v == null) return <span className="text-subtle">—</span>;
        const d = Number(v);
        return (
          <div>
            <b className={d > 14 ? 'tabular-nums text-danger-600 dark:text-danger-500' : d > 7 ? 'tabular-nums text-warning-600 dark:text-harvest-300' : 'tabular-nums text-muted'}>{d}d</b>
            {r.firstCollectedOn && <div className="type-caption text-subtle">since {formatIstDate(r.firstCollectedOn)}</div>}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Said in one sentence, because the number underneath is the one a Super Admin was previously
          shown their own ₹200 in place of. */}
      <div className="mb-4 flex items-start gap-2 rounded-control border border-sky-500/25 bg-sky-500/10 px-3 py-2 type-caption text-content">
        <Landmark size={14} className="mt-0.5 shrink-0 text-sky-600 dark:text-sky-400" />
        <span>
          Retailer cash <b>everyone</b> has collected that has not reached a company bank account yet —
          the whole company, not just you. Your own position is on <b>My collection money</b>.
        </span>
      </div>

      {/* Each tile opens exactly the rows it counts — its click REPLACES the filter, never stacks —
          and a tile with nothing behind it is not offered as one. */}
      <div className={KPI_ROW}>
        <StatCard
          label="Still out there" tone="warning" icon={<Banknote />}
          value={<span className="tabular-nums">{data ? inr(t.outstanding) : '—'}</span>}
          hint={holding.length
            ? `${holding.length} ${holding.length === 1 ? 'person is' : 'people are'} holding it${t.oldestAgeDays != null ? ` · oldest ${t.oldestAgeDays}d` : ''}`
            : 'nobody is holding company cash'}
          active={view === 'holding'}
          onClick={holding.length ? () => setView(view === 'holding' ? 'all' : 'holding') : undefined}
        />
        <StatCard
          label="Collected in all" tone="neutral" icon={<HandCoins />}
          value={<span className="tabular-nums">{data ? inr(t.collected) : '—'}</span>}
          hint={`every approved cash payment · ${holders.length} ${holders.length === 1 ? 'collector' : 'collectors'}`}
          active={view === 'all'}
          onClick={view === 'all' ? undefined : () => setView('all')}
        />
        <StatCard
          label="Reached the company" tone="brand" icon={<Landmark />}
          value={<span className="tabular-nums">{data ? inr(t.deposited) : '—'}</span>}
          hint={banked.length
            ? `banked by ${banked.length} of ${holders.length}`
            : 'nothing has been banked yet'}
          active={view === 'banked'}
          onClick={banked.length ? () => setView(view === 'banked' ? 'all' : 'banked') : undefined}
        />
        <StatCard
          label="Deposits nothing explains" tone={unallocated.length ? 'danger' : 'neutral'} icon={<ShieldQuestion />}
          value={data ? unallocated.length : '—'}
          hint={unallocated.length
            ? 'money in the bank with no collection behind it — open them'
            : 'every deposit is matched to a collection'}
          onClick={unallocated.length ? () => setShowUnallocated(true) : undefined}
        />
      </div>

      {/* The arithmetic the columns perform, stated once. Without it the row is five numbers that
          happen to sit together; with it, every figure can be checked against its neighbours. */}
      <p className="mb-2 type-caption text-muted">
        For each person: <b className="text-content">collected</b> − <b className="text-content">reached the company</b> −{' '}
        <b className="text-content">spent on approved claims</b> − <b className="text-content">moved into their wallet</b>{' '}
        = <b className="text-content">still with them</b>. Neither of the last two ever reaches a bank, and neither is
        owed back — click either figure to see the documents behind it.
      </p>

      <Card bodyClassName="p-0 sm:p-0">
        <DataTable
          storageKey="collection-money-holders"
          rowKey="staffId"
          loading={isLoading}
          dataSource={shown}
          columns={columns}
          layoutFixed
          pagination={shown.length > 25 ? { pageSize: 25 } : false}
          emptyText="Nobody is holding uncleared retailer cash"
          /* A row is a person, and the next question about a person is always "which payments?" —
             their own page carries the money card and the payment-by-payment breakdown behind these
             figures. The name is a link to the same place, so clicking anywhere on the row does what
             clicking the obvious part already did. */
          onRow={(r: any) => ({
            onClick: () => navigate(`/staff/${r.staffId}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/*
        * MONEY IN THE BANK THAT NO COLLECTION EXPLAINS.
        *
        * The endpoint has always returned these and nothing ever drew them. They are the other half of
        * the reconciliation: a deposit with no collection behind it is not a credit to net off somebody's
        * balance — it is a question. Reported, never subtracted.
        */}
      {showUnallocated && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setShowUnallocated(false); }}
          title="Deposits nothing explains"
          width={760}
          footer={<Button variant="default" onClick={() => setShowUnallocated(false)}>Close</Button>}
        >
          <p className="mb-3 type-body text-muted">
            Money that reached a company account without a collection to account for it. It is
            deliberately <b className="text-content">not</b> netted off anyone's balance — reducing
            somebody's accountability with an unexplained receipt is the silent adjustment this model
            exists to prevent.
          </p>
          <Table
            rowKey={(_: any, i?: number) => String(i)}
            size="small"
            dataSource={unallocated}
            pagination={false}
            locale={{ emptyText: 'Every deposit is matched to a collection' }}
            columns={[
              {
                title: 'Deposited', dataIndex: 'movementDate',
                render: (v: any, r: any) => <span className="whitespace-nowrap">{formatIstDate(v ?? r.date ?? r.createdAt)}</span>,
              },
              {
                title: 'Banked by', dataIndex: 'staffName',
                render: (v: any, r: any) => (v
                  ? <span className="inline-flex items-center gap-1.5"><UserLink id={r.staffId}>{v}</UserLink><RoleBadge role={r.staffRole} /></span>
                  : <span className="text-subtle">—</span>),
              },
              { title: 'Reference', dataIndex: 'reference', render: (v: any, r: any) => <span className="tabular-nums">{v ?? r.referenceNo ?? r.utr ?? '—'}</span> },
              {
                title: 'Unexplained', dataIndex: 'unallocated', align: 'right' as const,
                render: (v: any, r: any) => <b className="tabular-nums text-danger-600 dark:text-danger-500">{inr2(v ?? r.amount)}</b>,
              },
            ] as any}
          />
        </Modal>
      )}

      {spendFor && (
        <CollectionSpendModal
          row={spendFor.row}
          leg={spendFor.leg}
          onClose={() => setSpendFor(null)}
        />
      )}
    </div>
  );
}

/**
 * WHERE THE CASH WENT THAT NO BANK EVER SAW.
 *
 * Two of the four terms of the collection subtraction take money out of somebody's hands without a
 * deposit: an approved claim they paid for out of the cash, and cash turned into wallet float for an
 * approved advance. Both were totals with nothing behind them — the cell fell through to the row's
 * link and landed on the person's page, which does not carry either figure, so "₹2,434" could not be
 * traced to a single document.
 *
 * One dialog serves both legs because they are read from one endpoint and differ only in which
 * documents they name. The figure is re-totalled from the rows on screen rather than echoed from the
 * cell: if the list and the column ever disagreed, this is where it would have to show.
 */
function CollectionSpendModal({ row, leg, onClose }: { row: any; leg: 'CLAIM' | 'WALLET'; onClose: () => void }) {
  const navigate = useNavigate();
  const staffId = String(row.staffId);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['retailer-money', 'staff', staffId],
    queryFn: () => request<any>({ url: `/retailer-money/staff/${staffId}` }),
  });
  const rows: any[] = (data?.spend ?? []).filter((s: any) => s.leg === leg);
  const total = rows.reduce((n, s) => n + Number(s.amount ?? 0), 0);
  const isClaim = leg === 'CLAIM';

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isClaim ? 'Spent on approved claims' : 'Moved into their wallet'}
      width={780}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}
    >
      {/* Whose money, and which of their figures this is — the dialog is opened from a cell, and a
          cell carries neither once it is behind an overlay. */}
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 type-body">
          <UserLink id={row.staffId}>{row.staffName}</UserLink>
          <RoleBadge role={row.staffRole} />
        </span>
        <b className="type-metric-sm tabular-nums text-content">{inr2(total)}</b>
      </div>
      <p className="mb-3 type-body text-muted">
        {isClaim
          ? 'Approved expense claims they paid for out of the retailer cash they were holding. This money never reaches a bank and is not owed back, so it clears their balance alongside deposits.'
          : 'Retailer cash turned into wallet float for an approved advance. It is company float now — it was not spent and it is not a claim, so it leaves this book without being owed back.'}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : isError ? (
        /* Never draw "nothing here" over a request that failed — the one reading this is checking
           somebody's accountability, and an empty table would read as "they spent nothing". */
        <EmptyState
          icon={<Info />}
          title="Could not load the breakdown"
          description="The figure on the row still stands; this list could not be fetched. Close and try again."
        />
      ) : (
        <Table
          rowKey={(r: any, i?: number) => `${r.source}-${r.refId}-${i}`}
          size="small"
          dataSource={rows}
          pagination={false}
          locale={{ emptyText: isClaim ? 'No claims were paid from this cash' : 'No advances were funded from this cash' }}
          columns={[
            {
              /* The claim's own date rides UNDER its number rather than taking a column of its own:
                 five columns pushed "Cash used" off the right edge of the dialog, and a drill that
                 clips the amount it was opened to explain has failed at its one job. */
              title: isClaim ? 'Claim' : 'Advance', dataIndex: 'refNo',
              render: (v: any, r: any) => (
                <div className="min-w-0">
                  {v
                    ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums text-brand-700 hover:underline dark:text-brand-300"
                        onClick={() => (isClaim
                          ? navigate(`/expense-reports/${r.refId}`)
                          : navigate(`/wallets?view=requests&rep=${staffId}`))}
                      >
                        {v}
                      </button>
                    )
                    /* A consumption whose document was hard-deleted still spent the money. Say so rather
                       than printing a blank cell that looks like a rendering fault. */
                    : <span className="type-caption text-subtle">document no longer on file</span>}
                  {r.onDate && <div className="type-caption text-subtle">for {formatIstDate(r.onDate)}</div>}
                </div>
              ),
            },
            {
              /* The date the cash actually left their hands — the approval, not the claim. The two can
                 be weeks apart, and it is the approval that moved the balance. */
              title: 'Approved', dataIndex: 'happenedAt',
              render: (v: any) => <span className="whitespace-nowrap">{v ? formatIstDate(v) : <span className="text-subtle">—</span>}</span>,
            },
            {
              title: 'Status', dataIndex: 'refStatus',
              render: (v: any, r: any) => (
                <div className="flex flex-wrap items-center gap-1.5">
                  {v ? <Tag tone={(isClaim ? REPORT_STATUS_COLOR[v] : ADV_STATUS_COLOR[v]) ?? 'neutral'}>
                    {isClaim ? v.replace(/_/g, ' ').toLowerCase() : (ADV_STATUS_LABEL[v] ?? v)}
                  </Tag> : <span className="text-subtle">—</span>}
                  {/* Which book the row came out of. A pre-V2 claim carried the cash on its own record;
                      a V2 approval wrote a settlement. Same money, two different papers to ask for. */}
                  {r.settlementNo
                    ? <span className="type-caption tabular-nums text-subtle">{r.settlementNo}</span>
                    : r.source === 'LEGACY' ? <span className="type-caption text-subtle">pre-settlement claim</span> : null}
                </div>
              ),
            },
            {
              title: 'Cash used', dataIndex: 'amount', align: 'right' as const,
              render: (v: any) => <b className="tabular-nums text-content">{inr2(v)}</b>,
            },
          ] as any}
        />
      )}
    </Modal>
  );
}

function km(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, toR = (d: number) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat), dLng = toR(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// ── Fleet view: managers/admins see every rep's wallet ───────────────────────
function FleetView({ canRecord }: { canRecord: boolean }) {
  const navigate = useNavigate();
  const badges = useBadges();
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  // ── Filters, in the same order as the columns they sit under ──
  const [balance, setBalance] = useUrlString<string | undefined>('balance', undefined);
  const [role, setRole] = useUrlList('role');
  const [balanceMin, setBalanceMin] = useState<number | null>(null);
  const [balanceMax, setBalanceMax] = useState<number | null>(null);
  const [activeFrom, setActiveFrom] = useUrlDate('from');
  const [activeTo, setActiveTo] = useUrlDate('to');
  const [toursMin, setToursMin] = useState<number | null>(null);
  const [pendingTours, setPendingTours] = useUrlBool('tours');
  const [advanceMin, setAdvanceMin] = useState<number | null>(null);
  const [returnableMin, setReturnableMin] = useState<number | null>(null);
  const [awaitingApproval, setAwaitingApproval] = useUrlBool('awaiting');
  const [gps, setGps] = useUrlString<string | undefined>('gps', undefined);
  const [unverified, setUnverified] = useUrlBool('unverified');
  const balanceMinD = useDebounce(balanceMin, 400);
  const balanceMaxD = useDebounce(balanceMax, 400);
  const toursMinD = useDebounce(toursMin, 400);
  const advanceMinD = useDebounce(advanceMin, 400);
  const returnableMinD = useDebounce(returnableMin, 400);
  const [walletRepId, setWalletRepId] = useState<string | null>(null);
  const [adminPos, setAdminPos] = useState<{ lat: number; lng: number } | null>(null);
  const [giveFor, setGiveFor] = useState<any | null>(null);
  // Same permission the "Advances given" export already uses — exporting the register is a finance act,
  // separate from EXPENSE.VIEW which a Sales Head holds to read their team on screen.
  const canExportWallets = usePermission('EXPENSE.WALLET.EXPORT');
  const [walletExportOpen, setWalletExportOpen] = useState(false);
  const askMyLocation = () => {
    if (!navigator.geolocation) { message.warning('No geolocation in this browser'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setAdminPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => message.warning('Could not get your location'),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  };

  /*
   * Sort is SERVER truth: the endpoint orders the whole fleet and returns it ordered. Sorting in
   * the table would only reorder the page on screen, so "highest balance" would silently mean
   * "highest balance among the 25 rows you can see".
   */
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (search.trim()) p.search = search.trim();
    if (role.length) p.role = role.join(',');
    if (balance) p.balance = balance;
    if (balanceMinD != null) p.balanceMin = balanceMinD;
    if (balanceMaxD != null) p.balanceMax = balanceMaxD;
    if (activeFrom) p.activeFrom = activeFrom.format('YYYY-MM-DD');
    if (activeTo) p.activeTo = activeTo.format('YYYY-MM-DD');
    if (toursMinD != null) p.toursMin = toursMinD;
    if (pendingTours) p.pendingTours = 'true';
    if (advanceMinD != null) p.advanceMin = advanceMinD;
    if (returnableMinD != null) p.returnableMin = returnableMinD;
    if (awaitingApproval) p.awaitingApproval = 'true';
    if (gps) p.gps = gps;
    if (unverified) p.unverified = 'true';
    if (sort) p.sort = sort;
    return p;
  }, [search, role, balance, balanceMinD, balanceMaxD, activeFrom, activeTo, toursMinD,
      pendingTours, advanceMinD, returnableMinD, awaitingApproval, gps, unverified, sort]);

  const filtersActive = !!(search.trim() || balance || role.length || balanceMin != null || balanceMax != null
    || activeFrom || activeTo || toursMin != null || pendingTours || advanceMin != null
    || returnableMin != null || awaitingApproval || gps || unverified);
  const clearFilters = () => {
    setSearchInput(''); setBalance(undefined); setRole([]); setBalanceMin(null); setBalanceMax(null);
    setActiveFrom(null); setActiveTo(null); setToursMin(null); setPendingTours(false);
    setAdvanceMin(null); setReturnableMin(null); setAwaitingApproval(false); setGps(undefined);
    setUnverified(false);
  };

  /*
   * One chip per applied filter, each removable on its own. Without these the only way to find out
   * what was narrowing the list was to open the panel and read every control.
   */
  const BALANCE_LABEL: Record<string, string> = {
    POSITIVE: 'Float outstanding', NEGATIVE: 'Reimbursement due', ZERO: 'Settled',
  };
  const ROLE_LABEL: Record<string, string> = { SALES_PERSON: 'Sales person', SALES_HEAD: 'Sales head' };
  const chips: FilterChip[] = [
    ...(role.length ? [{ key: 'role', label: role.map((r) => ROLE_LABEL[r] ?? r).join(', '), onRemove: () => setRole([]) }] : []),
    ...(unverified ? [{ key: 'unverified', label: 'Unchecked entries', onRemove: () => setUnverified(false) }] : []),
    ...(balance ? [{ key: 'balance', label: BALANCE_LABEL[balance] ?? balance, onRemove: () => setBalance(undefined) }] : []),
    ...(balanceMin != null || balanceMax != null ? [{
      key: 'band',
      label: `${balanceMin != null ? inr(balanceMin) : 'any'} – ${balanceMax != null ? inr(balanceMax) : 'any'}`,
      onRemove: () => { setBalanceMin(null); setBalanceMax(null); },
    }] : []),
    ...(activeFrom || activeTo ? [{
      key: 'active',
      label: `Active ${activeFrom ? activeFrom.format('DD MMM') : 'any'} – ${activeTo ? activeTo.format('DD MMM') : 'any'}`,
      onRemove: () => { setActiveFrom(null); setActiveTo(null); },
    }] : []),
    ...(toursMin != null ? [{ key: 'tours', label: `Tours ≥ ${toursMin}`, onRemove: () => setToursMin(null) }] : []),
    ...(pendingTours ? [{ key: 'pending', label: 'Has an open visit', onRemove: () => setPendingTours(false) }] : []),
    ...(advanceMin != null ? [{ key: 'adv', label: `Advance ≥ ${inr(advanceMin)}`, onRemove: () => setAdvanceMin(null) }] : []),
    ...(returnableMin != null ? [{ key: 'ret', label: `Returnable ≥ ${inr(returnableMin)}`, onRemove: () => setReturnableMin(null) }] : []),
    ...(awaitingApproval ? [{ key: 'appr', label: 'Awaiting approval', onRemove: () => setAwaitingApproval(false) }] : []),
    ...(gps ? [{ key: 'gps', label: gps === 'true' ? 'Seen on the map' : 'Never seen', onRemove: () => setGps(undefined) }] : []),
  ];

  const { data: stats } = useQuery({
    queryKey: ['wallet-stats'],
    queryFn: () => request<any>({ url: '/wallets/stats' }),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['wallets', params],
    queryFn: () => request<any[]>({ url: '/wallets', params }),
    placeholderData: keepPreviousData,
  });
  /*
   * `?focus=<repId>` opens THAT rep's wallet, not just their row.
   *
   * A staff detail page links "Open wallet ledger" here; without this the reader landed on a list of
   * every field staff member and had to find the person they had just been looking at. Matched on
   * `repId` because that is the id the linking side knows — the row's own `id` is the wallet's.
   */
  const walletFocus = useFocusRow(data, { match: ['repId'], onFocus: (r) => setWalletRepId(String(r.repId)) });

  // `autoFit` on the table below: no column carries a width — each is sized from its own content
  // (heading included, so nothing wraps onto a second line) and the leftover width is shared out
  // across the columns as space BETWEEN them, rather than being parked in one dead strip after the
  // last column. Counts are CENTRED so a single digit sits under its heading instead of pinned to
  // the right edge of a column headed "Reports to approve", which reads as a hole in the row.
  // Money stays right-aligned so the figures line up on their decimal.
  /*
   * Search stays in the toolbar — it is the one filter people reach for first. The other twelve open
   * IN PLACE below it, grouped by the column they act on, in column order.
   */
  const filters = useFilterPanel({
    onClear: clearFilters,
    chips,
    groups: [
      {
        title: 'Field staff',
        fields: [
          {
            label: 'Role',
            control: (
              <Select mode="multiple" placeholder="Any role" allowClear
                value={role} onChange={(v) => setRole((v as string[]) ?? [])}
                options={[
                  { value: 'SALES_PERSON', label: 'Sales person' },
                  { value: 'SALES_HEAD', label: 'Sales head' },
                ]} />
            ),
          },
          {
            label: 'Ledger check',
            control: (
              <Select placeholder="Any" allowClear
                value={unverified ? 'true' : undefined}
                onChange={(v) => setUnverified(v === 'true')}
                options={[{ value: 'true', label: 'Has unchecked entries' }]} />
            ),
          },
        ],
      },
      {
        title: 'Current balance',
        fields: [
          {
            label: 'State',
            control: (
              <Select placeholder="Any balance" allowClear value={balance} onChange={setBalance}
                options={[
                  { value: 'POSITIVE', label: 'Float outstanding' },
                  { value: 'NEGATIVE', label: 'Reimbursement due' },
                  { value: 'ZERO', label: 'Settled' },
                ]} />
            ),
          },
          {
            label: 'Amount between',
            span: 2,
            control: (
              <FilterRange
                from={<InputNumber controls={false} placeholder="e.g. 1000" value={balanceMin}
                  onChange={(v) => setBalanceMin(v as number | null)} />}
                to={<InputNumber controls={false} placeholder="e.g. 5000" value={balanceMax}
                  onChange={(v) => setBalanceMax(v as number | null)} />}
              />
            ),
            hint: 'A negative amount is money the rep is owed.',
          },
        ],
      },
      {
        title: 'Last activity',
        fields: [
          {
            label: 'Active between',
            span: 2,
            control: (
              <FilterRange
                from={<DatePicker placeholder="from" allowClear disabledDate={disableFuture}
                  value={activeFrom} maxDate={activeTo ?? undefined}
                  onChange={(d: any) => setActiveFrom(d ?? null)} />}
                to={<DatePicker placeholder="to" allowClear disabledDate={disableFuture}
                  value={activeTo} minDate={activeFrom ?? undefined}
                  onChange={(d: any) => setActiveTo(d ?? null)} />}
              />
            ),
          },
          {
            label: 'GPS',
            control: (
              <Select placeholder="Any" allowClear value={gps} onChange={setGps}
                options={[
                  { value: 'true', label: 'Seen on the map' },
                  { value: 'false', label: 'Never seen' },
                ]} />
            ),
          },
        ],
      },
      {
        title: 'Tours & approvals',
        fields: [
          {
            label: 'Tours this month, at least',
            control: (
              <InputNumber controls={false} min={0} placeholder="e.g. 5" value={toursMin}
                onChange={(v) => setToursMin(v as number | null)} />
            ),
          },
          {
            label: 'Open visits',
            control: (
              <Select placeholder="Any" allowClear
                value={pendingTours ? 'true' : undefined}
                onChange={(v) => setPendingTours(v === 'true')}
                options={[{ value: 'true', label: 'Has an open visit' }]} />
            ),
            hint: 'Checked in, never checked out.',
          },
          {
            label: 'Tour advance, at least',
            control: (
              <InputNumber controls={false} min={0} placeholder="e.g. 1000" value={advanceMin}
                onChange={(v) => setAdvanceMin(v as number | null)} />
            ),
          },
          {
            label: 'Returnable, at least',
            control: (
              <InputNumber controls={false} min={0} placeholder="e.g. 1500" value={returnableMin}
                onChange={(v) => setReturnableMin(v as number | null)} />
            ),
          },
          {
            label: 'Reports to approve',
            control: (
              <Select placeholder="Any" allowClear
                value={awaitingApproval ? 'true' : undefined}
                onChange={(v) => setAwaitingApproval(v === 'true')}
                options={[{ value: 'true', label: 'Awaiting approval' }]} />
            ),
          },
        ],
      },
    ],
  });

  const columns: DataTableColumn<any>[] = [
    {
      key: 'repName', title: 'Field staff', dataIndex: 'repName', sortable: true, alwaysVisible: true, width: 260,
      /*
       * A face, then the name. This column carries the money that a PERSON is holding, and a reader
       * scanning a fleet recognises a photo before they read a name. The supporting line answers the
       * next two questions in place — how to reach them, and who they answer to — so the ordinary
       * "who is this?" no longer costs a click into the wallet.
       */
      render: (v: any, r: any) => (
        <div className="flex min-w-0 items-center gap-2.5 whitespace-nowrap">
          <Avatar src={r.repPhotoUrl ?? undefined} size={34} fallback={initials(v)} />
          <div className="min-w-0">
            <Space size={6}>
              <UserLink id={r.repId} role={r.repRole}>{v ?? `#${r.repId}`}</UserLink>
              <RoleTag role={r.repRole} />
              {r.repStatus && (
                <Tag tone={String(r.repStatus).toUpperCase() === 'ACTIVE' ? 'green' : 'red'}>
                  {String(r.repStatus).toLowerCase()}
                </Tag>
              )}
            </Space>
            <div className="type-caption text-subtle">
              {r.repPhone ?? '—'}
              {r.repTerritory ? ` · ${r.repTerritory}` : ''}
              {r.reportsToName ? ` · reports to ${r.reportsToName}` : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'currentBalance', title: 'Current balance', dataIndex: 'currentBalance', sortable: true, align: 'right', alwaysVisible: true, width: 132,
      render: (v: any) => (
        <Tooltip label={balanceLabel(v)}>
          {/* The one figure this row exists to show — the only money cell here that gets weight. */}
          <Money value={inr(v)} color={balanceColor(v)} strong />
        </Tooltip>
      ),
    },
    { key: 'repEmail', title: 'Email', dataIndex: 'repEmail', defaultVisible: false, render: (v: string) => v ? <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()} className="text-brand-700 hover:underline dark:text-brand-400">{v}</a> : '—' },
    { key: 'reportsToName', sortable: true, title: 'Reports to', dataIndex: 'reportsToName', defaultVisible: false, render: (v: string) => v ?? '—' },
    { key: 'repTerritory', sortable: true, title: 'Territory', dataIndex: 'repTerritory', defaultVisible: false, render: (v: string) => v ?? '—' },
    // The lifetime two-way total behind the current balance — how much has flowed through this rep,
    // which the single net figure deliberately hides.
    { key: 'totalIn', sortable: true, title: 'Total advanced', dataIndex: 'totalIn', align: 'right', defaultVisible: false, render: (v: any) => <Money value={inr(v)} /> },
    { key: 'totalOut', sortable: true, title: 'Total spent', dataIndex: 'totalOut', align: 'right', defaultVisible: false, render: (v: any) => <Money value={inr(v)} /> },
    { key: 'lastActivityAt', title: 'Last activity', dataIndex: 'lastActivityAt', sortable: true, width: 168, render: (v: string) => v ? <span className="whitespace-nowrap">{formatIst(v)}</span> : '—' },
    { key: 'toursMonth', sortable: true, title: 'Tours (month)', dataIndex: 'toursMonth', align: 'center', width: 96, render: (v: any) => v ?? 0 },
    { key: 'completedMonth', sortable: true, title: 'Completed (month)', dataIndex: 'completedMonth', align: 'center', defaultVisible: false, render: (v: any) => v ?? 0 },
    /*
     * "Pending tours" read as "visits scheduled but not yet done". It is not: the figure counts
     * visits the rep CHECKED IN to and never checked out of — an open visit, which is a data-hygiene
     * problem rather than a workload one. Naming it after what it counts is the whole fix.
     */
    {
      key: 'pendingTours', sortable: true, title: 'Open visits', dataIndex: 'pendingTours', align: 'center', width: 96,
      render: (v: any) => (
        <Tooltip label={v ? 'Checked in and never checked out' : 'Every visit was closed properly'}>
          <span>{v ? <Tag tone="gold">{v}</Tag> : 0}</span>
        </Tooltip>
      ),
    },
    { key: 'tourAdvance', sortable: true, title: 'Tour advance', dataIndex: 'tourAdvance', align: 'right', width: 130, render: (v: any) => Number(v) > 0 ? <Money value={inr(v)} color={PALETTE.brand[600]} /> : <span style={{ color: '#ccc' }}>—</span> },
    {
      /*
       * ONE column, ONE line, because a register is read down a column and every extra one makes the
       * rows taller and the table wider. Two columns here (a status and a last-payment) pushed the
       * row to four wrapped lines and squeezed its neighbours.
       *
       * It shows whichever of the two facts the reader can act on: if something is waiting on
       * somebody, that is the answer; if nothing is pending, the answer is how the last advance
       * actually reached them. The full picture stays one click away in the wallet.
       */
      key: 'advanceState', title: 'Advances', width: 178,
      render: (_: any, r: any) => {
        const approval = Number(r.advancesAwaitingApproval ?? 0);
        const payout = Number(r.advancesAwaitingPayout ?? 0);
        if (approval || payout) {
          /* Clickable: it opens the requests register filtered to this person, so "3 to pay · ₹605"
             leads to the three requests it is counting instead of ending the trail. */
          const label = payout
            ? `${payout} to pay · ${inr(r.advancesAwaitingPayoutAmt)}`
            : `${approval} to approve`;
          return (
            <button
              type="button"
              aria-label={`${label} — open the requests`}
              className="rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              onClick={(e) => { e.stopPropagation(); navigate(`/wallets?view=requests&rep=${r.repId}`); }}
            >
              <Tag tone={payout ? 'blue' : 'gold'} className="cursor-pointer whitespace-nowrap hover:brightness-95">{label}</Tag>
            </button>
          );
        }
        const a = r.lastAdvance;
        if (!a) return <span className="text-subtle">—</span>;
        const paidWith = [modeLabel(a.mode), a.bank].filter(Boolean).join(' · ');
        return (
          <span className="block truncate whitespace-nowrap" title={`${inr(a.amount)} · ${formatIstDate(a.at)}${paidWith ? ` · ${paidWith}` : ''}`}>
            <span className="tabular-nums text-content">{inr(a.amount)}</span>
            <span className="text-subtle"> · {modeLabel(a.mode) || 'mode not recorded'}</span>
          </span>
        );
      },
    },
    { key: 'returnable', sortable: true, title: 'Returnable', dataIndex: 'returnable', align: 'right', width: 122, render: (v: any) => Number(v) > 0 ? <Money value={inr(v)} /> : <span style={{ color: '#ccc' }}>—</span> },
    { key: 'reimbursementDue', sortable: true, title: 'Reimbursement due', dataIndex: 'reimbursementDue', align: 'right', defaultVisible: false, render: (v: any) => Number(v) > 0 ? <Money value={inr(v)} color={PALETTE.danger[500]} /> : <span style={{ color: '#ccc' }}>—</span> },
    {
      key: 'approvalSubmission', sortable: true, title: 'Reports to approve', dataIndex: 'approvalSubmission',
      align: 'center', width: 136,
      /*
       * That rep's EXPENSE REPORTS still sitting in SUBMITTED — money they have already spent, filed,
       * and which is now waiting on somebody's decision.
       *
       * It was headed "Approval submission" and printed as a bare, unclickable digit, naming neither
       * what was counted nor what to do about it. Asked directly on 2026-09-16, and then read as the
       * advance REQUESTS on the tab beside this one — a different object entirely (an ask for money
       * BEFORE it is spent, which never appears here). The heading now says what the number is, and
       * the number opens exactly the reports behind it instead of leaving the reader to find them.
       *
       * Zero is an em-dash, not a "0": in a row of money columns a bare digit reads as an amount.
       */
      render: (v: any, r: any) => {
        const n = Number(v ?? 0);
        if (!n) return <span style={{ color: '#ccc' }}>—</span>;
        const what = `${n} expense report${n === 1 ? '' : 's'} awaiting approval`;
        const who = r.repName ?? 'this rep';
        return (
          <Tooltip title={`Open ${who}'s ${what}`}>
            <button
              type="button"
              aria-label={`Open ${who}'s ${what}`}
              /* The ROW opens this rep's wallet; this cell opens a different screen, so it must not
                 do both. */
              onClick={(e) => { e.stopPropagation(); navigate(`/expense-reports?status=SUBMITTED&rep=${r.repId}`); }}
              className="inline-flex min-w-6 items-center justify-center rounded-full bg-sky-500/10 px-2 py-0.5 type-caption-strong leading-tight text-sky-600 transition-colors hover:bg-sky-500/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:text-sky-400"
            >
              {n}
            </button>
          </Tooltip>
        );
      },
    },
    {
      key: 'lastLocation', title: 'Last location', dataIndex: 'lastLocation', width: 152,
      render: (loc: any) => loc
        ? (
          <Space size={4} className="whitespace-nowrap">
            <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} aria-label="Open location in Google Maps" title="Open in Google Maps" className="inline-flex items-center text-brand-700 hover:text-brand-800 dark:text-brand-400"><MapPin size={14} /></a>
            {adminPos && <Tag tone="blue">~{km(adminPos, loc).toFixed(1)} km</Tag>}
            <span className="text-xs text-muted">{formatIstDate(loc.at)}</span>
          </Space>
        )
        : <span style={{ color: '#ccc' }}>no GPS</span>,
    },
    {
      /*
       * TWO ICONS, ONE LINE.
       *
       * At 104px — scaled to ~95px once the table shares out its width — a full-size button and a
       * small one did not fit side by side, so `Space wrap` folded the second onto its own line and
       * every row in the register grew to 91px tall to hold a stack of two icons. Sized for both, told
       * not to wrap, and both buttons on the 32px row-action scale the rest of the app uses.
       */
      key: 'actions', title: 'Action', dataIndex: 'id', alwaysVisible: true, width: 128,
      render: (_: any, r: any) => (
        <div className="flex flex-nowrap items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Open wallet"><Button size="sm" variant="text" aria-label="Open wallet" icon={<Eye size={16} />} onClick={() => setWalletRepId(String(r.repId))} /></Tooltip>
          {canRecord && <Tooltip label="Give advance"><Button size="sm" variant="primary" aria-label="Give advance" icon={<Plus size={16} />} onClick={() => setGiveFor(r)} /></Tooltip>}
        </div>
      ),
    },
  ];

  const advancesTab = (
    <>
      {/*
        * The KPI band, which doubles as the balance filter.
        *
        * Each card leads with the figure that decides whether to act — the MONEY, not the head count
        * — and names the count underneath. Three of the four correspond exactly to a balance state,
        * so clicking one filters the list to it and clicking it again clears the filter; the fourth
        * is a ledger-verification backlog with no list of its own, so it stays a read-out.
        */}
      <div className={KPI_ROW}>
        <StatCard
          label="Float outstanding"
          tone="brand"
          icon={<Wallet />}
          value={<span className="tabular-nums">{inr(stats?.total_float_outstanding)}</span>}
          hint={`${stats?.reps_with_float ?? 0} of ${stats?.reps_total ?? 0} staff${Number(stats?.oldest_float_days) > 0 ? ` · oldest ${stats?.oldest_float_days}d` : ''}${Number(stats?.largest_float) > 0 ? ` · largest ${inr(stats?.largest_float)}` : ''}`}
          active={balance === 'POSITIVE'}
          onClick={() => setBalance((b) => (b === 'POSITIVE' ? undefined : 'POSITIVE'))}
        />
        <StatCard
          label="Reimbursement due"
          tone="danger"
          icon={<HandCoins />}
          value={<span className={`tabular-nums ${Number(stats?.total_reimbursement_due) > 0 ? OUT_TEXT : ''}`}>{inr(stats?.total_reimbursement_due)}</span>}
          hint={Number(stats?.reimbursement_due) > 0 ? `${stats?.reimbursement_due} of ${stats?.reps_total ?? 0} staff out of pocket` : 'nobody is out of pocket'}
          active={balance === 'NEGATIVE'}
          onClick={() => setBalance((b) => (b === 'NEGATIVE' ? undefined : 'NEGATIVE'))}
        />
        <StatCard
          label="Settled"
          tone="sky"
          icon={<CheckCircle2 />}
          value={String(stats?.settled ?? 0)}
          hint={`of ${stats?.reps_total ?? 0} field staff · no money either way`}
          active={balance === 'ZERO'}
          onClick={() => setBalance((b) => (b === 'ZERO' ? undefined : 'ZERO'))}
        />
        <StatCard
          label="Awaiting verification"
          tone="gold"
          icon={<ShieldQuestion />}
          value={<span className="tabular-nums">{inr(stats?.unverified_amount)}</span>}
          hint={`${stats?.pending_verification ?? 0} ledger entries nobody has checked`}
          active={unverified}
          onClick={() => setUnverified((v) => !v)}
        />
      </div>

      <ExportDialog
        open={walletExportOpen}
        onClose={() => setWalletExportOpen(false)}
        title="Export field staff advances"
        description="Who is holding company cash right now, with float and reimbursement totals."
        baseUrl="/wallets/export"
        hidePeriod
        baseParams={params}
      />
      <Card>
        <DataTable
          storageKey="sp-advances"
          rowKey="id"
          autoFit
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(r: any) => ({ onClick: () => setWalletRepId(String(r.repId)), style: { cursor: 'pointer' } })}
          rowClassName={walletFocus.rowClassName}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          emptyText={<EmptyState title="No field staff" />}
          toolbarRight={canExportWallets && (
            <Button size="sm" icon={<Download size={16} />} onClick={() => setWalletExportOpen(true)}>Export</Button>
          )}
          toolbarLeft={
            <>
              <Input prefix={<Search size={16} />} placeholder="Name or phone" aria-label="Name or phone" className="w-[210px]" allowClear
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              {filters.trigger}
              <Tooltip label="Use your current location to show straight-line distance to each rep's last spot">
                <Button size="sm" variant={adminPos ? 'primary' : 'default'} icon={<MapPin size={16} />}
                  onClick={askMyLocation}>Distance from me</Button>
              </Tooltip>
              {filters.chips}
            </>
          }
          toolbarBelow={filters.panel}
        />
      </Card>
    </>
  );

  /*
   * `view`, not `tab`: WalletsPage already owns `?tab` for its own team/mine split, and a Sales Head
   * mounts BOTH — two hooks writing one param would fight each other on every click.
   */
  const [walletTab, setWalletTab] = useUrlTab('view', ['advances', 'history', 'requests'] as const, 'advances');

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>Field Staff Advances</Typography.Title>
      <Typography.Paragraph type="secondary">
        Per-rep running cash wallet (sales persons and sales heads). Admin tops up the float;
        submitted expense reports auto-deduct it.
      </Typography.Paragraph>
      {/* URL-driven so a deep-link can land on the right tab — an approvals-hub link to an advance
          used to open "Field staff advances" and leave the reader to find the requests tab itself. */}
      <Tabs
        activeKey={walletTab}
        onChange={(k) => setWalletTab(k as any)}
        items={[
          { key: 'advances', label: 'Field staff advances', children: advancesTab },
          { key: 'history', label: 'Advances given', children: <AdvancesGivenTab onOpenWallet={setWalletRepId} /> },
          {
            key: 'requests',
            label: (
              <Space size={6}>
                Requests
                {!!badges.advanceRequestsAwaitingMe && <Tag tone="red">{badges.advanceRequestsAwaitingMe}</Tag>}
              </Space>
            ),
            children: <AdvanceRequestsPage embedded />,
          },
        ]}
      />
      {walletRepId && (
        <WalletModal repId={walletRepId} canRecord={canRecord} canVerify={canRecord} onClose={() => setWalletRepId(null)} />
      )}
      {giveFor && <GiveAdvanceModal rep={giveFor} onClose={() => setGiveFor(null)} />}
    </div>
  );
}

// ── "Advances given": the cross-rep history ──────────────────────────────────
// Both ways money reaches a wallet in ONE list — a direct "Give advance" and one disbursed
// from an approved request — so you no longer have to open each rep's wallet in turn.
function AdvancesGivenTab({ onOpenWallet }: { onOpenWallet: (repId: string) => void }) {
  // Exporting the whole register is its own permission (Admin/Accounts/SA by default, editable on
  // Roles & Permissions) — a Sales Head can read their team's rows without taking the file.
  const canExport = usePermission('EXPENSE.WALLET.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);
  const [searchInput, setSearchInput] = useUrlString('lq');
  const search = useDebounce(searchInput, 400);
  const [repId, setRepId] = useUrlString<string | undefined>('rep', undefined);
  const [type, setType] = useState<string>('ADVANCE_IN');
  const [mode, setMode] = useState<string | undefined>();
  const [range, setRange] = useUrlRangeOrNull('range');
  /* Which KPI card is switched on. One at a time: they are alternative cuts of the same money
     (bank / cash / untraced / reversed), so holding two would ask for rows that cannot exist. */
  const [card, setCard] = useUrlString<'BANK' | 'CASH' | 'UNTRACED' | 'REVERSED' | undefined>('card', undefined);
  const toggleCard = (c: typeof card) => setCard((cur) => (cur === c ? undefined : c));
  /* Server-side, and under its own URL key (`sort` belongs to the Field staff advances tab). The list is
     capped at 500, so a browser sort only reordered the newest 500 — "oldest first" was never the
     oldest — and the export could not have listed the same rows in the same order. */
  const { sortState, onSortChange, sort } = useServerSort({ urlKey: 'asort' });

  /* Which row is open as a record. The register row IS the advance, so clicking it opens the
     advance — not the rep's whole ledger, which answers a different question and is a button
     inside the dialog. */
  const [detailRow, setDetailRow] = useState<any | null>(null);

  const { data: reps } = useQuery({
    queryKey: ['wallets', {}],
    queryFn: () => request<any[]>({ url: '/wallets' }),
  });

  const params = useMemo(() => {
    const p: Record<string, any> = { type };
    if (search.trim()) p.search = search.trim();
    if (repId) p.repId = repId;
    if (mode) p.mode = mode;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    if (card === 'BANK' || card === 'CASH') p.paidVia = card;
    else if (card === 'REVERSED') p.reversed = 'true';
    else if (card === 'UNTRACED') p.untraced = 'true';
    if (sort) p.sort = sort;
    return p;
  }, [type, search, repId, mode, range, card, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['wallet-advances', params],
    queryFn: () => request<any>({ url: '/wallets/advances', params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.rows ?? [];
  const meta = data?.meta;
  const summary = meta?.summary;

  const filtersActive = !!(search.trim() || repId || mode || range || card || type !== 'ADVANCE_IN');
  const clearFilters = () => { setSearchInput(''); setRepId(undefined); setMode(undefined); setRange(null); setType('ADVANCE_IN'); setCard(undefined); };

  /* What the export will contain, in words. The dialog takes no filters of its own — it sends the ones
     above (card and sort included) — so this is where the reader checks the file before taking it. */
  const CARD_WORDS: Record<string, string> = {
    BANK: 'Through a bank', CASH: 'Cash', UNTRACED: 'Untraced (names no bank account)', REVERSED: 'Reversed',
  };
  const exportFilters = [
    repId && `Given to ${(reps ?? []).find((r: any) => String(r.repId) === String(repId))?.repName ?? `#${repId}`}`,
    mode && `Paid via ${modeLabel(mode)}`,
    card && CARD_WORDS[card],
    search.trim() && `Matching "${search.trim()}"`,
  ].filter(Boolean) as string[];
  const exportScope = (
    <div className="space-y-1">
      <ScopeRow label="Entries">
        {TYPE_LABEL[type] ?? type} · {rows.length.toLocaleString('en-IN')} {rows.length === 1 ? 'entry' : 'entries'} — what the list below is showing
        {meta?.capped && <span className="text-muted"> (the file is not capped at {meta.limit})</span>}
      </ScopeRow>
      <ScopeRow label="Period">{range ? `${range[0].format('DD MMM YYYY')} – ${range[1].format('DD MMM YYYY')}` : 'All time'}</ScopeRow>
      <ScopeRow label="Filters">{exportFilters.length ? exportFilters.join(' · ') : 'None'}</ScopeRow>
      <ScopeRow label="Order">
        {sortState ? `${sortState.key === 'amount' ? 'Amount' : 'Date'}, ${sortState.dir === 'asc' ? 'ascending' : 'descending'}` : 'Newest first'}
      </ScopeRow>
    </div>
  );

  const columns: DataTableColumn<any>[] = [
    { key: 'txnNo', title: 'Txn No', dataIndex: 'txnNo', alwaysVisible: true, width: 150 },
    /* "Given on", not "Date": the Source column next to it now carries the date the advance was
       REQUESTED, and two dates on one row cannot both be "the date". */
    { key: 'txnDate', title: 'Given on', dataIndex: 'txnDate', sortable: true, render: (v: string) => formatIstDate(v) },
    {
      key: 'repName', title: 'Given to', dataIndex: 'repName', alwaysVisible: true, width: 200,
      render: (v: any, r: any) => (
        <Space size={6} wrap>
          <span className="font-medium">{v ?? `#${r.repId}`}</span>
          <RoleTag role={r.repRole} />
        </Space>
      ),
    },
    {
      key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', sortable: true, alwaysVisible: true,
      // A reversed row is money that came back — strike it so it never reads as given.
      render: (v: any, r: any) => (
        <Money value={inr2(v)} color={r.reversed ? '#8c8c8c' : PALETTE.brand[600]} strong={!r.reversed} strike={!!r.reversed} />
      ),
    },
    {
      /* WHEN IT WAS ASKED FOR, next to the number it was asked under.
         A payout row is dated the day the money left, which is the only date this register used to
         show — so "how long did this rep wait?" could not be answered here at all, and the reader
         had to carry the ADVR number over to the Requests tab to find out. The request's own date
         travels with the row now; a Direct advance was never requested, so it says nothing. */
      key: 'source', title: 'Source', dataIndex: 'referenceType', width: 210,
      render: (v: string, r: any) => v === 'ADVANCE_REQUEST'
        ? (
          <div className="min-w-0 space-y-0.5">
            <Space size={4} wrap><Tag tone="cyan">On request</Tag><span style={{ fontSize: 12, color: '#888' }}>{r.requestNo}</span></Space>
            {r.requestedAt && <div className="type-caption text-subtle">Requested {formatIstDate(r.requestedAt)}</div>}
          </div>
        )
        : <Tag tone="neutral">Direct</Tag>,
    },
    {
      key: 'paymentMode', title: 'Paid via', dataIndex: 'paymentMode',
      render: (v: any, r: any) => v
        ? <Space size={4} wrap><Tag>{modeLabel(v)}</Tag>{r.paymentReference && <span style={{ fontSize: 12, color: '#888' }}>{r.paymentReference}</span>}</Space>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      key: 'recordedByName', title: 'Given by', dataIndex: 'recordedByName',
      render: (v: any, r: any) => v ? <Space size={4} wrap>{v}<RoleTag role={r.postedByRole} name={v} /></Space> : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      key: 'purpose', title: 'Purpose / note', dataIndex: 'notes', ellipsis: true,
      // On a disbursed request the note is the auto "Advance ADVR-…", which the Source column
      // already shows — the rep's stated PURPOSE is the useful text there.
      render: (v: any, r: any) =>
        (r.referenceType === 'ADVANCE_REQUEST' ? (r.requestPurpose ?? v) : v) ?? r.reason ?? <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      /* A proof is a PICTURE. "View" was a word that meant "leave this page, open a tab, and find
         out whether the file is even the right document" — and it could only ever reach ONE file,
         while an advance raised on a request has two: the receipt for the payment, and the bill or
         quotation the rep attached when they asked. Both hang off this row, so both are SHOWN here
         (the shared cell only falls back to a +N badge past two files), each under its own name —
         they are different documents, produced by different people, at different moments. */
      key: 'attachmentUrl', title: 'Proof', dataIndex: 'attachmentUrl', align: 'center', width: 88,
      render: (v: any, r: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell
            files={[v, r.requestProofUrl]}
            labels={['Payment proof', r.requestNo ? `Attached to request ${r.requestNo}` : 'Attached to the request']}
            label="Proof"
            size={32}
          />
        </span>
      ),
    },
    {
      key: 'reversed', title: 'Status', dataIndex: 'reversed', width: 110,
      render: (v: any) => v ? <Tag tone="red">Reversed</Tag> : <Tag tone="green">Given</Tag>,
    },
  ];

  return (
    <>
      {/* Four figures a finance reader actually asks of a disbursement register: how much left the
          company, how much came back, how much of it can be traced to a bank account, and how many
          people it reached. All four are computed SERVER-side over every matching row — the previous
          pair were summed from the rows the page happened to receive, so past the 500-row cap they
          silently understated the money given out. */}
      {/* Five cuts of the same money, and every one of them is a filter: click a card, the register
          below shows exactly the rows behind that number, click it again to come back. Bank and cash
          are SEPARATE cards, not one figure with the other in its caption — they reconcile against
          different things (a statement line vs a voucher), so adding them together says nothing.
          The figures are computed server-side over every matching row and deliberately IGNORE the
          card filters, so a card never zeroes itself out and stops being clickable. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label={`${TYPE_LABEL[type] ?? type} — net given`}
          tone="brand"
          icon={<HandCoins />}
          value={<span className={`tabular-nums ${IN_TEXT}`}>{inr(summary?.netAmount)}</span>}
          hint={card ? 'show all' : `${summary?.entries ?? 0} entries · ${summary?.staffCount ?? 0} staff`}
          active={!card}
          onClick={() => setCard(undefined)}
        />
        <StatCard
          label="Through a bank"
          tone="sky"
          icon={<Landmark />}
          value={<span className="tabular-nums">{inr(summary?.bankAmount)}</span>}
          hint="UPI · transfer · cheque · card"
          active={card === 'BANK'}
          onClick={() => toggleCard('BANK')}
        />
        <StatCard
          label="Cash"
          tone="gold"
          icon={<Banknote />}
          value={<span className="tabular-nums">{inr(summary?.cashAmount)}</span>}
          hint="handed over in person"
          active={card === 'CASH'}
          onClick={() => toggleCard('CASH')}
        />
        {/* The reconciliation gap, as its own number: non-cash money naming no account of ours. */}
        <StatCard
          label="Untraced"
          tone={Number(summary?.untracedAmount) > 0 ? 'warning' : 'neutral'}
          icon={<ShieldQuestion />}
          value={<span className={`tabular-nums ${Number(summary?.untracedAmount) > 0 ? 'text-warning-600 dark:text-harvest-300' : 'text-subtle'}`}>{inr(summary?.untracedAmount)}</span>}
          hint={Number(summary?.untracedCount) > 0 ? `${summary?.untracedCount} name no bank account` : 'every payout is attributed'}
          active={card === 'UNTRACED'}
          onClick={() => toggleCard('UNTRACED')}
        />
        <StatCard
          label="Reversed"
          tone={Number(summary?.reversedAmount) > 0 ? 'danger' : 'neutral'}
          icon={<Undo2 />}
          value={<span className={`tabular-nums ${Number(summary?.reversedAmount) > 0 ? OUT_TEXT : 'text-subtle'}`}>{inr(summary?.reversedAmount)}</span>}
          hint={Number(summary?.reversedCount) > 0 ? `${summary?.reversedCount} clawed back` : 'nothing clawed back'}
          active={card === 'REVERSED'}
          onClick={() => toggleCard('REVERSED')}
        />
      </div>

      {meta?.capped && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Showing the latest {meta.limit} entries — narrow the date range to see the rest.
        </Typography.Paragraph>
      )}

      <Card>
        <DataTable
          storageKey="advances-given"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          onRow={(r: any) => ({ onClick: () => setDetailRow(r), style: { cursor: 'pointer' } })}
          emptyText={<EmptyState title="No advances given yet" />}
          toolbarRight={canExport && (
            <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          )}
          toolbarLeft={
            <Space wrap>
              <Input prefix={<Search size={16} />} placeholder="Txn no, ref, note or name" aria-label="Txn no, ref, note or name" className="w-[220px]"
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <Select placeholder="Type" className="w-[170px]" value={type} onChange={(v: any) => setType(v ?? 'ADVANCE_IN')}
                options={TXN_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] ?? t }))} />
              <Select showSearch optionFilterProp="label" allowClear placeholder="Field staff" className="w-[190px]"
                value={repId} onChange={setRepId}
                options={(reps ?? []).map((r: any) => ({ value: String(r.repId), label: r.repName ?? `#${r.repId}` }))} />
              <Select allowClear placeholder="Paid via" className="w-[150px]" value={mode} onChange={setMode}
                options={PAYMENT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
              {/* The picker only emits COMPLETE ranges (see dateRange.ts), hence the cast. */}
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r as [Dayjs, Dayjs] | null)} />
              {filtersActive && <FilterResetButton size="md" onClick={clearFilters} />}
            </Space>
          }
        />
      </Card>
      {detailRow && (
        <AdvanceDetailModal row={detailRow} onClose={() => setDetailRow(null)} onOpenWallet={onOpenWallet} />
      )}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export advances given"
        description="The entries this register is showing — its filters, the card switched on and the column order."
        baseUrl="/wallets/advances"
        formats={['pdf', 'csv']}
        hidePeriod
        baseParams={params}
        scopeSummary={exportScope}
      />
    </>
  );
}


// ── Modal: ONE advance, in full ──────────────────────────────────────────────
/** One field of the record: sentence-case label over a 14px/600 value (see ui/SectionHead). */
function AField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 ${className ?? ''}`}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-0.5 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

/** One stage of the request behind an advance: a coloured mark, what happened, and when. */
function TrailStep({ tint, icon, title, when, children }: {
  tint: string; icon: ReactNode; title: ReactNode; when?: ReactNode; children?: ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-md ${tint} [&>svg]:size-3.5`}>{icon}</span>
      <div className="min-w-0 flex-1 pb-3">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="type-body-strong text-content">{title}</span>
          {when && <span className="type-caption text-muted">{when}</span>}
        </div>
        {children && <div className="mt-0.5 type-caption text-muted">{children}</div>}
      </div>
    </li>
  );
}

/**
 * The advances register's row, opened as the RECORD it is.
 *
 * The row click used to open the rep's whole wallet ledger — the right answer to "what else has this
 * person had", the wrong one to "tell me about THIS payout", which is what a reader clicking a
 * particular amount on a particular date is asking. The ledger is still one button away in the
 * footer; this dialog answers the row.
 *
 * It opens FILLED from the row already in hand, and the fetch only adds what a column could not
 * hold — the company account the money left from, who has checked it, the decision behind the
 * request, and the entry that clawed it back. So no spinner ever covers facts the register printed.
 */
function AdvanceDetailModal({ row, onClose, onOpenWallet }: {
  row: any; onClose: () => void; onOpenWallet: (repId: string) => void;
}) {
  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: ['wallet-advance', String(row?.id)],
    queryFn: () => request<any>({ url: `/wallets/advances/${row.id}` }),
    enabled: !!row?.id,
  });
  const a = { ...row, ...(data ?? {}) };
  const req = a.request ?? null;

  // tinyint: `reversed` arrives as 0/1, and 0 is falsy but renders as "0" the moment it reaches JSX.
  const reversed = !!Number(a.reversed);
  const isReversalEntry = !!a.reversalOfTxnId;
  const cash = a.paymentMode === 'CASH';
  // The "Untraced" KPI card, per row: real money off a bank rail naming no account of ours, so it
  // can never be matched to a statement line.
  const untraced = !!a.paymentMode && !cash && !a.bankAccountId && !reversed;
  const bankLine = a.bankAccountId
    ? [a.bankLabel || a.bankName, a.bankAccountNumber ? `····${String(a.bankAccountNumber).slice(-4)}` : null].filter(Boolean).join(' · ')
    : null;

  // How long the money has been out. A float is only a problem once it is OLD, and the age is what
  // turns an amount into a reason to chase it — the reading the fleet tab's "oldest float" takes.
  const endOfOut = reversed ? (a.reversal?.txnDate ?? a.reversal?.createdAt) : undefined;
  const outDays = (() => {
    const from = dayjs(a.txnDate);
    const to = endOfOut ? dayjs(endOfOut) : dayjs();
    if (!from.isValid() || !to.isValid()) return null;
    return Math.max(0, to.startOf('day').diff(from.startOf('day'), 'day'));
  })();
  const dayWord = (n: number) => (n === 0 ? 'Same day' : `${n} ${n === 1 ? 'day' : 'days'}`);

  // What was asked vs what was funded: an approver may pay less than the ask, and that gap is the
  // one thing a reader of the payout cannot work out from the payout.
  const asked = req ? Number(req.amount) : null;
  const shortFunded = asked != null && Math.abs(asked - Number(a.amount)) > 0.005;
  const fromCash = Number(req?.cashFundedAmount ?? 0);

  // Proof of the PAYMENT. The ledger's own attachment and the one kept on the request are usually
  // the same upload, so they are de-duplicated rather than shown as two receipts.
  const proofs = Array.from(new Set([a.attachmentUrl, req?.paymentProofUrl].filter(Boolean))) as string[];

  const purpose = (a.referenceType === 'ADVANCE_REQUEST' ? (req?.purpose ?? a.requestPurpose) : null) ?? a.notes ?? a.reason ?? null;
  // The auto note on a disbursed request only restates its number, which the header already carries.
  const extraNote = a.notes && a.notes !== purpose && !/^Advance ADVR/i.test(String(a.notes)) ? a.notes : null;

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={920}
      // Nothing here is typed into, so focus stays on the dialog rather than popping the tooltip of
      // whichever copy-chip happens to come first.
      autoFocus={false}
      title={TYPE_LABEL[a.type] ?? typeLabel(a.type)}
      description="Who received it, how it was paid, and the paperwork behind it."
      footer={
        <>
          <Button variant="default" onClick={onClose}>Close</Button>
          <Button variant="primary" icon={<Wallet size={16} />}
            onClick={() => { onClose(); onOpenWallet(String(a.repId)); }}>
            Open wallet ledger
          </Button>
        </>
      }
    >
      {/* Letterhead — which entry this is and what state it is in. The money is NOT here: it is the
          first tile of the band below, and a figure appears once on a screen. */}
      <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-card p-3 ring-1 ring-inset ${
        reversed
          ? 'bg-linear-to-r from-danger-500/10 via-danger-500/5 to-transparent ring-danger-500/15'
          : 'bg-linear-to-r from-brand-600/10 via-brand-500/5 to-transparent ring-brand-600/10'
      }`}>
        <span className={`grid size-12 shrink-0 place-items-center rounded-2xl text-white shadow-e1 [&>svg]:size-6 ${
          reversed ? 'bg-danger-600' : 'bg-brand-600'
        }`}>
          {reversed ? <Undo2 /> : <HandCoins />}
        </span>
        <div className="min-w-0">
          {/* The number people paste into a message or a bank portal, so it is one click, not a drag. */}
          <CopyValue value={a.txnNo} label="Txn no">
            <span className="type-section-title text-content">{a.txnNo}</span>
          </CopyValue>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Tag tone={reversed ? 'red' : 'green'}>{reversed ? 'Reversed' : 'Given'}</Tag>
            {a.referenceType === 'ADVANCE_REQUEST'
              ? <Tag tone="cyan">On request {req?.requestNo ?? a.requestNo}</Tag>
              : <Tag tone="neutral">Direct — no request</Tag>}
            {untraced && <Tag tone="gold">Untraced</Tag>}
            {/* The date the MONEY moved. The keystroke that recorded it is a different fact and is
                named separately under Paper trail. */}
            <span className="type-caption text-muted">given {formatIstDate(a.txnDate)}</span>
          </div>
        </div>
      </div>

      {/* A clawed-back advance changes how every figure under it reads, so it is said before them
          rather than left as a chip to notice. */}
      {reversed && (
        <div className="mb-4 flex items-start gap-2.5 rounded-card border border-danger-500/30 bg-danger-500/[0.07] px-3.5 py-3">
          <Undo2 className="mt-0.5 size-4 shrink-0 text-danger-600 dark:text-danger-500" aria-hidden />
          <div className="min-w-0 type-caption text-content">
            <span className="type-body-strong">This advance came back.</span>{' '}
            {a.reversal ? (
              <>
                {inr(a.reversal.amount)} was clawed back on {formatIstDate(a.reversal.txnDate)}
                {a.reversal.recordedByName ? ` by ${a.reversal.recordedByName}` : ''} under{' '}
                <CopyValue value={a.reversal.txnNo} label="Reversal txn no"><span className="font-medium">{a.reversal.txnNo}</span></CopyValue>.
                {a.reversal.reason ? ` Reason: ${a.reversal.reason}` : ''}
              </>
            ) : (
              <>It no longer counts towards money given out. {isFetching ? 'Loading the reversing entry…' : 'The reversing entry is not on file.'}</>
            )}
          </div>
        </div>
      )}
      {isReversalEntry && (
        <div className="mb-4 flex items-start gap-2.5 rounded-card border border-line bg-surface-2/60 px-3.5 py-3">
          <Info className="mt-0.5 size-4 shrink-0 text-subtle" aria-hidden />
          <div className="min-w-0 type-caption text-content">
            This entry <span className="type-body-strong">undoes</span>{' '}
            {a.reverses
              ? <>{a.reverses.txnNo} of {formatIstDate(a.reverses.txnDate)} ({inr(a.reverses.amount)})</>
              : 'an earlier advance'}.
          </div>
        </div>
      )}

      {/* The four figures a reader asks of one payout: how much left, what the wallet stood at right
          after, what is with the rep now, and how long it has been out. */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          tone={reversed ? 'danger' : 'brand'}
          icon={<HandCoins />}
          label="Amount given"
          value={<span className={`tabular-nums ${reversed ? `line-through ${OUT_TEXT}` : IN_TEXT}`}>{inr(a.amount)}</span>}
          hint={req ? (shortFunded ? `of ${inr(asked)} asked` : 'the full ask') : typeLabel(a.type)}
        />
        <StatCard
          tone="sky"
          icon={<Wallet />}
          label="Wallet after this"
          value={<span className="tabular-nums">{inr(a.balanceAfter)}</span>}
          hint="float with them that moment"
        />
        <StatCard
          tone={Number(a.repBalanceNow) < 0 ? 'danger' : Number(a.repBalanceNow) > 0 ? 'brand' : 'neutral'}
          icon={<Users />}
          label="With them today"
          value={<span className={`tabular-nums ${balanceTextCls(a.repBalanceNow)}`}>{a.repBalanceNow == null ? '—' : inr(a.repBalanceNow)}</span>}
          hint={a.repBalanceNow == null ? 'wallet still loading' : balanceLabel(a.repBalanceNow)}
        />
        <StatCard
          tone={!reversed && (outDays ?? 0) > 30 ? 'warning' : 'neutral'}
          icon={<History />}
          label={reversed ? 'Was out for' : 'Out for'}
          value={outDays == null ? '—' : dayWord(outDays)}
          hint={reversed ? (endOfOut ? `until ${formatIstDate(endOfOut)}` : 'until it was reversed') : 'since the money went out'}
        />
      </div>

      {/* A panel that failed to load must not read as a record with nothing in it. */}
      {isError && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-card border border-warning-500/35 bg-warning-500/10 px-3.5 py-2.5 type-caption text-content">
          <ShieldQuestion className="size-4 shrink-0 text-warning-600 dark:text-harvest-300" aria-hidden />
          <span>Couldn’t load the account, approval and verification trail — what is below is the register row only.</span>
          <Button size="sm" variant="default" onClick={() => refetch()}>Try again</Button>
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* WHO got the money — the dialog is about a payout, and a payout has a person at the end of it. */}
        <section className="min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
          <SectionHead icon={<Users className="size-3.5" />} tint={SECTION_TINT.identity}>Given to</SectionHead>
          <div className="flex items-center gap-3">
            <Avatar src={a.repPhotoUrl} size={44} fallback={<span className="type-body-strong">{initials(a.repName)}</span>} />
            <div className="min-w-0">
              <div className="truncate type-body-strong text-content">{a.repName ?? `#${a.repId}`}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <RoleTag role={a.repRole} name={a.repName} />
                {a.repTerritory && (
                  <span className="inline-flex items-center gap-1 rounded-pill bg-violet-500/10 px-2 py-0.5 type-caption text-violet-700 dark:text-violet-300">
                    <MapPin className="size-3" aria-hidden />{a.repTerritory}
                  </span>
                )}
              </div>
            </div>
          </div>
          {a.repPhone && (
            <dl className="mt-3">
              <AField label="Mobile">
                {/* A number on a field-staff record exists to be rung, so it is the control that rings it. */}
                <a href={`tel:${a.repPhone}`}
                  className="inline-flex items-center gap-1.5 rounded-pill bg-emerald-500/10 px-2.5 py-1 type-body-medium text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-400">
                  <Phone className="size-3.5" aria-hidden />{a.repPhone}
                </a>
              </AField>
            </dl>
          )}
        </section>

        {/* HOW it was paid — the rail, the reference to match it by, and the account it left. */}
        <section className="min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
          <SectionHead icon={<Landmark className="size-3.5" />} tint={SECTION_TINT.money}>How it was paid</SectionHead>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <AField label="Paid via">
              {a.paymentMode ? <Tag tone={cash ? 'gold' : 'blue'}>{modeLabel(a.paymentMode)}</Tag> : undefined}
            </AField>
            <AField label={cash ? 'Voucher / reference' : 'Reference / UTR'}>
              {a.paymentReference
                ? (
                  <CopyValue value={a.paymentReference} label="Payment reference">
                    <span className="font-mono text-[13px]">{a.paymentReference}</span>
                  </CopyValue>
                )
                : undefined}
            </AField>
            <AField label="From which account" className="col-span-2">
              {cash
                ? <span className="font-normal text-muted">Cash — it left the till, not an account</span>
                : bankLine
                  ? <span>{bankLine}{a.bankIfsc ? <span className="ml-1.5 font-normal text-subtle">{a.bankIfsc}</span> : null}</span>
                  : untraced
                    ? (
                      <span className="inline-flex items-start gap-1.5 text-warning-600 dark:text-harvest-300">
                        <ShieldQuestion className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                        Names no company account — this payout cannot be matched to a statement line
                      </span>
                    )
                    : undefined}
            </AField>
            <AField label="Payment proof" className="col-span-2">
              {proofs.length
                ? <AttachmentCell files={proofs} label="Payment proof" size={44} />
                : <span className="font-normal text-subtle">Nothing uploaded</span>}
            </AField>
          </dl>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* WHY — the rep's stated purpose, in their words, at a size it can actually be read at. */}
        <section className="min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
          <SectionHead icon={<FileText className="size-3.5" />} tint={SECTION_TINT.documents}>What it is for</SectionHead>
          {purpose
            ? <p className="whitespace-pre-wrap type-body-medium text-content">{purpose}</p>
            : <p className="type-caption text-subtle">No purpose was recorded against this entry.</p>}
          {extraNote && (
            <dl className="mt-3 border-t border-line/70 pt-3">
              <AField label="Note from the payer">{extraNote}</AField>
            </dl>
          )}
          {a.reason && a.reason !== purpose && (
            <dl className="mt-3 border-t border-line/70 pt-3">
              <AField label="Reason">{a.reason}</AField>
            </dl>
          )}
        </section>

        {/* WHO recorded it, and whether anyone has since checked it. */}
        <section className="min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
          <SectionHead icon={<History className="size-3.5" />} tint={SECTION_TINT.system}>Paper trail</SectionHead>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <AField label="Given by">
              {a.recordedByName
                ? <Space size={4} wrap>{a.recordedByName}<RoleTag role={a.postedByRole} name={a.recordedByName} /></Space>
                : undefined}
            </AField>
            {/* Named apart from "given": one is when the money moved, the other when someone typed it
                in, and an advance can be entered days after it was handed over. */}
            <AField label="Entered on">{formatIst(a.createdAt)}</AField>
            <AField label="Checked">
              <Space size={4} wrap>
                <Tag tone={VERIFY_COLOR[a.verificationStatus] ?? 'neutral'}>
                  {VERIFY_FILTER_LABEL[a.verificationStatus] ?? a.verificationStatus ?? '—'}
                </Tag>
                {a.verifiedByName && <span className="type-caption text-muted">{a.verifiedByName}</span>}
              </Space>
            </AField>
            <AField label="Checked on">{a.verifiedAt ? formatIst(a.verifiedAt) : undefined}</AField>
          </dl>
        </section>
      </div>

      {/* The DECISION behind the money, when there was one. A direct advance has no request, and the
          section simply isn't there — better than an empty panel that reads as a failed load. */}
      {a.referenceType === 'ADVANCE_REQUEST' && (
        <section className="mt-4 min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
          <SectionHead icon={<CheckCircle2 className="size-3.5" />} tint={SECTION_TINT.assignment}
            hint={req ? ADV_STATUS_LABEL[req.status] ?? req.status : undefined}>
            The request behind it
          </SectionHead>
          {req ? (
            <ol>
              <TrailStep tint={SECTION_TINT.identity} icon={<FileText />}
                title={`Asked for ${inr(req.amount)}`} when={formatIst(req.requestedAt)}>
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                  <CopyValue value={req.requestNo} label="Request no">
                    <span className="font-medium text-content">{req.requestNo}</span>
                  </CopyValue>
                  {req.requestProofUrl && <AttachmentCell files={[req.requestProofUrl]} label="Justification" size={28} />}
                </span>
              </TrailStep>
              <TrailStep tint={SECTION_TINT.money} icon={<CheckCircle2 />}
                title={req.approvedAt ? `Approved${shortFunded ? ` for ${inr(a.amount)}` : ''}` : 'Not approved yet'}
                when={req.approvedAt ? formatIst(req.approvedAt) : undefined}>
                {req.approvedByName ? `by ${req.approvedByName}` : 'approver not recorded'}
                {shortFunded && <> · {inr(Number(asked) - Number(a.amount))} less than the ask</>}
                {fromCash > 0 && <> · {inr(fromCash)} came from collection cash they already held</>}
              </TrailStep>
              <TrailStep tint={SECTION_TINT.contact} icon={<ArrowUpRight />}
                title="Paid out" when={formatIst(req.disbursedAt ?? a.createdAt)}>
                {req.disbursedByName ? `by ${req.disbursedByName}` : a.recordedByName ? `by ${a.recordedByName}` : 'payer not recorded'}
                {' · posted as '}{a.txnNo}
              </TrailStep>
            </ol>
          ) : (
            <p className="type-caption text-muted">
              {isFetching ? 'Loading the request…' : 'The request this was paid against is no longer on file.'}
            </p>
          )}
        </section>
      )}
    </Modal>
  );
}

// Admin gives an advance straight into a sales person's wallet (posts ADVANCE_IN). The
// request → approve → disburse flow still lives on the 'Pending requests' tab for the
// approval-gated path (e.g. a Sales Head requesting on behalf).
function GiveAdvanceModal({ rep, onClose }: { rep: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [mode, setMode] = useState('BANK_TRANSFER');
  const [bankId, setBankId] = useState<string | undefined>();
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [proof, setProof] = useState<string | undefined>();
  // The company accounts kept in Company & Billing setup — the advance has to name the one it came from.
  // ACTIVE accounts only (same options endpoint the payment screens use, primary first): the ledger
  // rejects an inactive or unknown account, so offering one would only produce an error.
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });
  const save = useConfirmedMutation({ title: "Give this advance?", content: "The money is recorded as paid out and lands on the rep’s wallet straight away.", okText: "Yes, give it" }, {
    mutationFn: () => request({
      url: `/wallets/${rep.repId}/transactions`, method: 'POST',
      data: {
        type: 'ADVANCE_IN', amount: Number(amount), paymentMode: mode,
        bankAccountId: mode === 'CASH' ? undefined : bankId,
        paymentReference: ref || undefined, notes: note || undefined,
        attachmentUrl: proof || undefined,
      },
    }),
    onSuccess: () => {
      message.success('Advance given');
      qc.invalidateQueries({ queryKey: ['wallets'] });
      qc.invalidateQueries({ queryKey: ['wallet-stats'] });
      qc.invalidateQueries({ queryKey: ['wallet', String(rep.repId)] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }}
      // A non-cash advance can't be saved without naming the account it came from — the server would
      // refuse it anyway, so the button says so first.
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0) || (mode !== 'CASH' && (!bankId || !proof))} onClick={() => save.mutate()}>Give advance</Button></>}
      title={<span>Give advance <Tag tone="blue">{rep.repName ?? `#${rep.repId}`}</Tag></span>}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        Posts an ADVANCE_IN directly into the wallet (float). Current balance <Money value={inr(rep.currentBalance)} strong />.
      </Typography.Paragraph>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        <Space wrap align="center">
          <span style={{ color: '#888' }}>Paid via</span>
          <Select value={mode} onChange={(v) => { setMode(v); if (v === 'CASH') setBankId(undefined); }} className="w-[150px]"
            options={['CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER'].map((m) => ({ value: m, label: m }))} />
          {/* Which company account the advance leaves from — the accounts kept in Company & Billing
              setup. Cash leaves no account, so the picker only applies to the other modes; the ledger
              refuses a non-cash movement that doesn't name one. */}
          {mode !== 'CASH' && (
            <Select
              className="w-[220px]" placeholder="From which bank account" value={bankId} onChange={setBankId}
              options={(bankAccounts ?? []).map((a: any) => ({
                value: String(a.id),
                label: `${a.label || a.bankName}${a.last4 ? ` · ····${a.last4}` : ''}`,
              }))}
              notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup"
            />
          )}
          <Input placeholder="Ref / UTR" value={ref} onChange={(e) => setRef(e.target.value)} className="w-40" />
        </Space>
        <Textarea rows={2} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
        {/* This dialog posts the same ADVANCE_IN the request-disbursement path does, so it carries
            the same obligation: a non-cash payout names its account AND shows its receipt. It had no
            upload at all, which made it the one door money could leave by without evidence. */}
        <FileUploadField
          category="receipt"
          buttonLabel={mode !== 'CASH' ? 'Upload payment proof (required)' : 'Upload proof (optional)'}
          value={proof} onChange={setProof}
        />
      </Space>
    </Modal>
  );
}

// ── Modal: one rep's wallet header + ledger ──────────────────────────────────
function WalletModal({ repId, canRecord, canVerify, onClose }: { repId: string; canRecord: boolean; canVerify: boolean; onClose: () => void }) {
  const [recordOpen, setRecordOpen] = useState(false);
  const { data: wallet, isLoading } = useQuery({
    queryKey: ['wallet', repId],
    queryFn: () => request<any>({ url: `/wallets/${repId}` }),
  });

  return (
    <>
      <Modal
        open
        onOpenChange={(o) => { if (!o) onClose(); }}
        width={1000}
        // The dialog is ABOUT a person, so the person is the title — name at page-title size with
        // their avatar, role and what this wallet is on the line beneath. It used to read
        // "Wallet [Test - SP]", which made the one thing you opened the dialog to identify the
        // smallest text in the header.
        title={
          <span className="flex min-w-0 items-center gap-3">
            <Avatar size={40} src={wallet?.repPhotoUrl} fallback={<span className="type-body-strong">{initials(wallet?.repName)}</span>} />
            <span className="min-w-0">
              <span className="block truncate type-page-title text-content">{wallet?.repName ?? `#${repId}`}</span>
              <span className="mt-0.5 flex items-center gap-1.5 type-caption font-normal text-muted">
                Cash wallet
                <RoleTag role={wallet?.repRole} name={wallet?.repName} />
              </span>
            </span>
          </span>
        }
        footer={
          <>
            {canRecord && (
              <Button variant="primary" icon={<Plus size={16} />} onClick={() => setRecordOpen(true)}>Record transaction</Button>
            )}
            <Button variant="default" onClick={onClose}>Close</Button>
          </>
        }
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : !wallet ? (
          <EmptyState title="Wallet not found" />
        ) : (
          <WalletBody wallet={wallet} repId={repId} canVerify={canVerify} />
        )}
      </Modal>
      {/* Sibling of the wallet modal, not a child: keeping a second Radix dialog out of the
          first one's subtree avoids the two fighting over the focus trap. */}
      {recordOpen && <RecordTxnModal repId={repId} onClose={() => setRecordOpen(false)} />}
    </>
  );
}

function WalletBody({ wallet, repId, canVerify, showLedger = true, showSummary = true }: { wallet: any; repId: string; canVerify: boolean; showLedger?: boolean; showSummary?: boolean }) {
  /*
   * RETAILER MONEY, fetched alongside the wallet but never mixed into it.
   *
   * Somebody looking at this drawer is looking at company float. The other thing they are often about
   * to ask is "and how much dealer cash is this person sitting on?" — a question the wallet cannot
   * answer, because retailer collections never touch it. Showing it here saves the trip; showing it in
   * its own strip below the wallet tiles is what stops it being read as a fifth kind of float.
   */
  const retailerMoney = useQuery({
    queryKey: ['staff-retailer-money', repId],
    queryFn: () => request<{ collected: number; deposited: number; outstanding: number }>(
      { url: `/retailer-money/staff/${repId}` },
    ),
    enabled: !!repId,
    retry: false,
  });
  /* Tile-driven ledger filters, held here because the tiles and the table are siblings. Same pattern
     as every other KPI band in the app (Payments, Invoices, the wallets list itself): a tile toggles
     its own filter and lights up while it is on. Only the two that ARE a set of rows filter —
     Current balance is the net of the other two, so it resets instead. */
  const [ledgerType, setLedgerType] = useState<string | undefined>();
  const [ledgerDir, setLedgerDir] = useState<string | undefined>();
  const toggle = (set: (v: any) => void, cur: string | undefined, next: string) => set(cur === next ? undefined : next);
  const clearTiles = () => { setLedgerType(undefined); setLedgerDir(undefined); };
  const tileFiltered = !!(ledgerType || ledgerDir);
  return (
    <>
      {/* Money band. The three figures mean three different things, so they are told apart by
          COLOUR AND DIRECTION, not just by their labels: money that came in is green and prefixed
          `+`, money that went out is red and prefixed `−`, exactly as each ledger row below states
          it. They previously sat in one grey run of `Total in ₹58,166  Total out ₹58,166`, where the
          only way to tell an inflow from an outflow was to read the label — and identical amounts
          (a fully-settled wallet) made the two look like the same number printed twice.
          The balance keeps its own sign colouring plus the word for what the sign MEANS. */}
      {showSummary ? (
        <>
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {/* The balance is not a set of entries — it is what the other two net to — so it resets the
              ledger rather than filtering it, and never sits in the lit "filtered" state. Its hint
              keeps saying what the SIGN means, which is the one thing a balance must always say. */}
          <StatCard
            label="Current balance"
            tone={statTone(balanceColor(wallet.currentBalance))}
            icon={<Wallet />}
            value={<span className={`tabular-nums ${balanceTextCls(wallet.currentBalance)}`}>{inr(wallet.currentBalance)}</span>}
            hint={tileFiltered ? 'show all entries' : balanceLabel(wallet.currentBalance)}
            onClick={showLedger ? clearTiles : undefined}
          />
          <StatCard
            label="Total in"
            tone="brand"
            icon={<ArrowDownLeft />}
            value={<span className={`tabular-nums ${IN_TEXT}`}>+{inr(wallet.totalIn)}</span>}
            hint={ledgerDir === 'IN' ? 'listed below' : 'every inflow'}
            active={ledgerDir === 'IN'}
            onClick={showLedger ? () => toggle(setLedgerDir, ledgerDir, 'IN') : undefined}
          />
          <StatCard
            label="Total out"
            tone="danger"
            icon={<ArrowUpRight />}
            value={<span className={`tabular-nums ${OUT_TEXT}`}>−{inr(wallet.totalOut)}</span>}
            hint={ledgerDir === 'OUT' ? 'listed below' : 'every outflow'}
            active={ledgerDir === 'OUT'}
            onClick={showLedger ? () => toggle(setLedgerDir, ledgerDir, 'OUT') : undefined}
          />
          {/* The wallets list has a "Tour advance" column, and someone who opens this dialog off the
              back of that column has to be able to FIND that number here. It is not any of the three
              figures above — it is one KIND of inflow, so "Total in" is larger and nothing else
              matches. Naming it exactly as the column does and saying what it is part of answers the
              question; clicking it filters the ledger to the very entries it sums. */}
          {/* Just the figure and what it is. It was briefly clickable, toggling a composite filter on
              the ledger — one number carrying a hidden behaviour, a selected state and a bespoke
              filter value was more machinery than the number is worth. Anyone who wants the rows
              picks a Type in the filter row below.
              `?? 0` would be a LIE: a server that doesn't send this field (an older build) is not a
              wallet with no advances, and ₹0 is indistinguishable from a real zero. */}
          <StatCard
            label="Tour advance"
            tone="gold"
            icon={<HandCoins />}
            value={wallet.tourAdvance == null
              ? <span className="text-subtle">—</span>
              : <span className={`tabular-nums ${IN_TEXT}`}>{inr(wallet.tourAdvance)}</span>}
            hint={wallet.tourAdvance == null
              ? 'not reported by the server'
              // When something WAS returned, say so: the tile is below the advances it lists, and an
              // unexplained gap between a figure and its own rows is exactly what makes a reader
              // stop trusting the screen.
              : Number(wallet.tourReturns ?? 0) > 0
                ? `less ${inr(wallet.tourReturns)} returned`
                : ledgerType === 'ADVANCE_IN' ? 'listed below' : 'advances − returns'}
            active={ledgerType === 'ADVANCE_IN'}
            onClick={showLedger && wallet.tourAdvance != null ? () => toggle(setLedgerType, ledgerType, 'ADVANCE_IN') : undefined}
          />
        </div>

        {/*
          * A DIFFERENT BOOK, drawn to look like one.
          *
          * Everything above is company float — money released to this person to spend. This is dealer
          * cash they collected and the company has not received yet. The two are never added, so the
          * strip sits outside the tile grid with its own rule above it: a fifth tile in that row would
          * have read as another kind of float, which is the single most expensive misreading on this
          * screen.
          *
          * Hidden entirely when there is nothing and never has been, so a desk user who has never
          * touched cash does not get a ₹0 they have to interpret.
          */}
        {retailerMoney.data && Number(retailerMoney.data.collected) > 0.005 && (
          <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-card border border-line bg-surface-2/40 px-4 py-3">
            <span className="type-overline text-subtle">Collection money outstanding</span>
            <b className={`type-metric-sm tabular-nums ${
              Number(retailerMoney.data.outstanding) > 0.005 ? 'text-warning-600 dark:text-harvest-300' : 'text-subtle'
            }`}>
              {inr(retailerMoney.data.outstanding)}
            </b>
            <span className="type-caption text-subtle">
              {inr(retailerMoney.data.collected)} collected · {inr(retailerMoney.data.deposited)} deposited
            </span>
            <span className="type-caption text-muted">
              — not yet deposited to the company. Separate from the wallet above; the two are never added.
            </span>
          </div>
        )}
        </>
      ) : (
        // Compact form for "My wallet", where this block sits inside a Card — a tile there would be
        // a card inside a card.
        <div className="mb-4">
          <div className="text-xs text-muted">Current balance</div>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="tabular-nums leading-tight" style={{ fontSize: 30, fontWeight: 600, color: balanceColor(wallet.currentBalance) }}>
              {inr(wallet.currentBalance)}
            </span>
            <Tag tone={balanceTone(wallet.currentBalance)}>{balanceLabel(wallet.currentBalance)}</Tag>
          </div>
        </div>
      )}

      {showLedger && (
        // The heading and the export go INTO the table's own toolbar, on the row that already
        // carries the column-picker — otherwise that control lands on a line of its own under the
        // heading and reads as an orphan.
        <LedgerTable
          repId={repId}
          repName={wallet.repName}
          lastTxnAt={wallet.lastTxnAt}
          transactions={wallet.transactions ?? []}
          canVerify={canVerify}
          type={ledgerType}
          onTypeChange={setLedgerType}
          direction={ledgerDir}
          onDirectionChange={setLedgerDir}
        />
      )}
    </>
  );
}

/**
 * Export this rep's ledger as a PDF statement or plain CSV rows — exactly the entries the ledger is
 * showing: its filters, the tile switched on, and the Date order. The dialog asks for nothing of its
 * own; `scope` says in words what the file will hold. Unlike the screen it is not capped at 500.
 * Hidden without EXPENSE.WALLET.EXPORT.
 */
function ExportLedgerButton({ repId, repName, params, scope }: {
  repId: string; repName?: string | null; params: Record<string, any>; scope: ReactNode;
}) {
  const canExport = usePermission('EXPENSE.WALLET.EXPORT');
  const [open, setOpen] = useState(false);
  if (!canExport) return null;
  return (
    <>
      <Button size="sm" icon={<Download size={16} />} onClick={() => setOpen(true)}>Export</Button>
      <ExportDialog
        open={open}
        onClose={() => setOpen(false)}
        title={repName ? `Export ${repName}'s ledger` : 'Export wallet ledger'}
        description="The entries this ledger is showing — its filters and order."
        baseUrl={`/wallets/${repId}/ledger`}
        formats={['pdf', 'csv']}
        hidePeriod
        baseParams={params}
        scopeSummary={scope}
      />
    </>
  );
}

function LedgerTable({ repId, repName, lastTxnAt, transactions, canVerify, type, onTypeChange, direction, onDirectionChange }: {
  repId: string; repName?: string | null; lastTxnAt?: string | null; transactions: any[]; canVerify: boolean;
  /** Controlled by WalletBody so the money tiles can drive the list. */
  type?: string; onTypeChange: (t: string | undefined) => void;
  direction?: string; onDirectionChange: (d: string | undefined) => void;
}) {
  const qc = useQueryClient();
  const [editRow, setEditRow] = useState<any>(null);
  const [verifyRow, setVerifyRow] = useState<any>(null);
  const [correctionRow, setCorrectionRow] = useState<{ row: any; req: any } | null>(null);
  const inval = () => {
    qc.invalidateQueries({ queryKey: ['wallet', repId] });
    qc.invalidateQueries({ queryKey: ['wallet-txns', repId] });
    qc.invalidateQueries({ queryKey: ['wallet-corrections', repId] });
    qc.invalidateQueries({ queryKey: ['wallet-stats'] });
    qc.invalidateQueries({ queryKey: ['wallets'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const verify = useConfirmedMutation({ title: "Verify this wallet entry?", content: "It is treated as settled and stops showing as pending.", okText: "Yes, verify" }, {
    mutationFn: (txnId: string) => request({ url: `/wallets/${repId}/transactions/${txnId}/verify`, method: 'POST' }),
    onSuccess: () => { message.success('Transaction verified'); inval(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const del = useMutation({
    mutationFn: (txnId: string) => request({ url: `/wallets/${repId}/transactions/${txnId}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Transaction reversed'); inval(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  /* ── Filters ──────────────────────────────────────────────────────────────────────────────────
   * These query the FULL ledger (`/wallets/:id/transactions`), not the last-20 window the wallet
   * payload carries. Filtering that window would have answered "no adjustments this year" from a
   * fortnight of rows — a filter that quietly lies is worse than no filter. Every clause is applied
   * server-side, so the endpoint's 500-row cap bounds the MATCHES rather than the window they are
   * searched in, and the summary line below counts what the filter actually selected.
   * ------------------------------------------------------------------------------------------ */
  const [searchInput, setSearchInput] = useUrlString('tq');
  const search = useDebounce(searchInput, 300);
  const setType = onTypeChange;
  const [verification, setVerification] = useUrlString<string | undefined>('verif', undefined);
  const [from, setFrom] = useUrlDate('lfrom');
  const [to, setTo] = useUrlDate('lto');
  const clearFilters = () => {
    setSearchInput(''); setType(undefined); setVerification(undefined); setFrom(null); setTo(null);
    onDirectionChange(undefined);
  };
  const anyFilter = !!(searchInput.trim() || type || direction || verification || from || to);
  /* The Date order is applied by the SERVER (own URL key — `sort` belongs to the register behind this
     dialog). The ledger read is capped at 500, so sorting in the browser answered "oldest first" with
     the oldest of the newest 500, and the export could never have matched it. */
  const { sortState, onSortChange, sort } = useServerSort({ urlKey: 'lsort' });
  const params = {
    search: search.trim() || undefined,
    type,
    direction,
    verificationStatus: verification,
    from: from ? from.format('YYYY-MM-DD') : undefined,
    to: to ? to.format('YYYY-MM-DD') : undefined,
    sort,
  };
  /* EVERY correction on this wallet, not just the ones still open. An applied or rejected request is
     the record of a decision taken on this money — fetching only the open ones meant the history
     existed in the database and nowhere a reader could reach it. */
  const { data: corrections } = useQuery({
    queryKey: ['wallet-corrections', repId],
    queryFn: () => request<any[]>({ url: '/wallets/corrections', params: { repId } }),
  });
  const forTxn = (txnId: string) => (corrections ?? []).filter((c: any) => String(c.txnId) === String(txnId));
  /** The one still in flight, if any — that is the row's ACTION. */
  const openFor = (txnId: string) => forTxn(txnId).find((c: any) => c.status === 'PENDING' || c.status === 'APPROVED');
  /** Otherwise the newest closed one — that is the row's HISTORY. */
  const closedFor = (txnId: string) => forTxn(txnId).find((c: any) => c.status === 'APPLIED' || c.status === 'REJECTED');

  const { data: fullLedger, isFetching } = useQuery({
    queryKey: ['wallet-txns', repId, params],
    queryFn: () => request<any[]>({ url: `/wallets/${repId}/transactions`, params }),
    placeholderData: keepPreviousData,
  });
  // The embedded last-20 renders instantly on open; the full history replaces it a moment later.
  const rows: any[] = fullLedger ?? transactions;
  const capped = rows.length >= LEDGER_CAP;
  // What the current selection adds up to — the point of filtering a ledger is usually the total,
  // and reading it off the rows means it can never disagree with them.
  const totals = useMemo(
    () => rows.reduce((a, r: any) => {
      const n = Number(r.amount ?? 0);
      if (r.direction === 'IN') a.in += n; else a.out += n;
      return a;
    }, { in: 0, out: 0 }),
    [rows],
  );

  // Verification only applies to rep-self-logged rows. When a wallet has none — the common case,
  // since admin and system postings are trusted — the column is 100% dashes, so drop it entirely
  // rather than reserve 150px for nothing.
  const showVerifyCol = rows.some((t: any) => t.verificationStatus && t.verificationStatus !== 'NOT_REQUIRED');

  const columns: DataTableColumn<any>[] = [
    /* Widths on every column shown by default: without them each falls back to a 150px slot, the
       seven together overflow the dialog, and the row's LAST action ends up beyond the right edge —
       reachable only by scrolling, which is how a reader ends up seeing one button and not the rest. */
    { key: 'txnNo', title: 'Txn No', dataIndex: 'txnNo', alwaysVisible: true, width: 148 },
    { key: 'txnDate', title: 'Date', dataIndex: 'txnDate', sortable: true, width: 112, render: (v: string) => formatIstDate(v) },
    { key: 'type', title: 'Type', dataIndex: 'type', width: 132, render: (v: string) => <Tag tone={TXN_TYPE_COLOR[v]}>{typeLabel(v)}</Tag> },
    {
      // Direction is already said three ways — this chip, the +/− sign, and the amount's colour.
      // Keep it available in the column picker, but off by default so the row reads once.
      key: 'direction', title: 'Dir', dataIndex: 'direction', defaultVisible: false,
      render: (v: string) => <Tag tone={v === 'IN' ? 'green' : 'red'}>{v}</Tag>,
    },
    {
      key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', width: 112,
      // The amount is what each ledger row is about, so it alone carries weight here.
      render: (v: any, r: any) => (
        <Money value={`${r.direction === 'IN' ? '+' : '−'}${inr(v)}`} color={r.direction === 'IN' ? PALETTE.brand[600] : PALETTE.danger[500]} strong />
      ),
    },
    // Running context, not the headline — quiet, so the eye follows one column down the table.
    { key: 'balanceAfter', title: 'Balance after', dataIndex: 'balanceAfter', align: 'right', width: 124, render: (v: any) => <Money value={inr(v)} color={balanceColor(v)} /> },
    {
      // "UPI" alone answers HOW the money moved but not out of WHERE, which is the question a reader
      // reconciling a bank statement is actually asking. Account first, then the reference that
      // identifies the transfer on that account's statement.
      key: 'paymentMode', title: 'Mode', dataIndex: 'paymentMode', defaultVisible: false,
      render: (v: any, r: any) => (v
        ? [modeLabel(v), r.bankLabel, r.paymentReference].filter(Boolean).join(' · ')
        : '—'),
    },
    ...(showVerifyCol ? [{
      key: 'verificationStatus', title: 'Verify', dataIndex: 'verificationStatus', alwaysVisible: true, width: 96,
      /* STATE ONLY — the Verify action lives in the row's action cluster with Edit and Reverse.
         It used to sit here, a filled green button in a column of its own several columns to the LEFT
         of the others: on a table wide enough to scroll, the confirming click was the first (sometimes
         the only) control a reader met, and the edit/reverse it should have been weighed against were
         off the right edge. Actions belong together in one place, in one fixed order. */
      render: (v: string) => (
        v === 'NOT_REQUIRED' ? <span className="text-muted">—</span>
          : v === 'VERIFIED' ? (
            <span className="inline-flex items-center gap-1 text-muted">
              <Check size={14} className="text-brand-600 dark:text-brand-400" /> Verified
            </span>
          ) : <span className="text-muted">Not checked</span>
      ),
    } as DataTableColumn<any>] : []),
    {
      /* The receipt itself, as a thumbnail that opens full-size in place (and a "+N" badge where an
         entry carries more than one file). A "View" link made every reader open a tab just to find
         out whether the right document was attached — on a verification queue that is the whole
         question. Shown by default: the proof is the point of these rows. */
      key: 'attachmentUrl', title: 'Proof', dataIndex: 'attachmentUrl', align: 'center', width: 82,
      render: (v: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={v ? [v] : []} label="Proof" size={32} />
        </span>
      ),
    },
    { key: 'notes', title: 'Notes', dataIndex: 'notes', defaultVisible: false, ellipsis: true, render: (v: any, r: any) => v ?? r.reason ?? '—' },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 116,
      render: (_: any, r: any) => {
        if (r.reversed) return <Tag tone="neutral">Reversed</Tag>;
        // Only manually-posted, non-reversal rows can be edited/reversed (backend enforces too).
        /* A rep's self-logged cash entry is their STATEMENT of what they spent. It is posted as a
           MANUAL row, which used to make it editable — an admin could quietly change the amount
           someone else declared, leaving only an audit-log line behind. Corrections to a declaration
           go through a reversal (which stays on the ledger) instead, so Edit is withheld here while
           Reverse is not. */
        const repDeclared = r.type === 'REP_CASH_OUT' || r.type === 'REP_CASH_IN';
        const canManage = canVerify && r.referenceType === 'MANUAL' && !r.reversalOfTxnId;
        const canEdit = canManage && !repDeclared;
        const open = openFor(r.id);
        const past = !open ? closedFor(r.id) : undefined;
        const canMarkVerified = canVerify && r.verificationStatus === 'UNVERIFIED';
        if (!canManage && !canMarkVerified && !open && !past) return null;
        return (
          // Bare icons (no filled/bordered button): a solid pair sat taller than the text rows
          // around it and stretched every ledger row. Same treatment as the Payments list.
          /* Fixed rank: correction → the forward step → destructive, never push-ordered, so the same
             icon is always in the same place and position can carry half the meaning. Verify keeps its
             confirm, and sits second — a pointer landing on the cluster meets the harmless action
             first and the reversal last. */
          <Space wrap size={4} onClick={(e) => e.stopPropagation()}>
            {canEdit && !open && (
              <Tooltip label="Propose a correction">
                <Button size="sm" variant="text" aria-label="Propose a correction" icon={<Pencil size={16} />} onClick={() => setEditRow(r)} />
              </Tooltip>
            )}
            {past && (
              /* History: this entry has been through a correction. Quiet on purpose — it is not an
                 action, it is a record — but reachable, so "who changed this and who approved it"
                 is one click from the figure rather than a database query. */
              <Tooltip label={`Correction ${String(past.status).toLowerCase()} — see the trail`}>
                <Button size="sm" variant="text" aria-label="Correction history"
                  icon={<History size={16} className="text-subtle" />}
                  onClick={() => setCorrectionRow({ row: r, req: past })} />
              </Tooltip>
            )}
            {open && (
              /* One button, whatever the stage: a pending request opens for review (the Super Admin
                 decides there), an approved one opens to be applied. Either way the row stops
                 offering a second proposal, because there is already one in flight. */
              <Tooltip label={open.status === 'PENDING' ? 'Correction awaiting Super Admin' : 'Approved correction — apply it'}>
                <Button size="sm" variant={open.status === 'APPROVED' ? 'primary' : 'text'}
                  aria-label={open.status === 'PENDING' ? 'Correction awaiting Super Admin' : 'Apply approved correction'}
                  icon={<ShieldQuestion size={16} className={open.status === 'APPROVED' ? undefined : 'text-warning-600 dark:text-harvest-300'} />}
                  onClick={() => setCorrectionRow({ row: r, req: open })} />
              </Tooltip>
            )}
            {canMarkVerified && (
              /* Opens the entry for REVIEW rather than asking "are you sure?" about a row the
                 approver cannot see. This is a rep's own declaration of money they spent out of the
                 float — the whole point of the step is to look at what they wrote and the receipt
                 they attached, and a Popconfirm shows neither. */
              <Tooltip label="Review and verify">
                <Button size="sm" variant="text" aria-label="Review and verify" icon={<Check size={16} className="text-brand-600 dark:text-brand-400" />}
                  onClick={() => setVerifyRow(r)} />
              </Tooltip>
            )}
            {canManage && (
              <Popconfirm
                title="Reverse this transaction?"
                description="Posts an offsetting entry (net zero). The original stays for audit."
                okText="Reverse" okButtonProps={{ danger: true, loading: del.isPending }}
                onConfirm={() => del.mutate(r.id)}
              >
                <Tooltip label="Delete (reverse)"><Button size="sm" variant="text" aria-label="Delete (reverse)" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} /></Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {/* Heading, and what the current selection comes to. The totals line is how a figure on the
          list gets traced: filter Type to "Advance given" and it states, in the same place, how many
          entries make up the advance and what they sum to. */}
      <div className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="type-body-strong text-content">Ledger</div>
          {/* When the wallet last moved belongs beside the entries, not up among the money tiles:
              it is a fact about this list, and a date is not a KPI. */}
          <div className="type-caption text-muted">
            {lastTxnAt ? `Last entry ${formatIst(lastTxnAt)}` : 'No entries yet'}
          </div>
        </div>
        {/* Export sits up here with the count, not in the table toolbar: it is an action on the
            ledger as a whole, and keeping it out of that row leaves the filters a single line
            instead of pushing the export and column-picker onto one of their own. */}
        <div className="flex items-center gap-3">
          {/* The in/out pair appears ONLY while a filter is on, where it plainly means "this
              selection". Shown unfiltered it read as a second opinion on the tiles above — two
              different in/out figures a hand's width apart, which is a question the reader cannot
              answer. (The tiles are the wallet's stored lifetime totals; this is the sum of the
              entries listed. They agree unless a wallet's running total has drifted from its own
              ledger — a data fault worth catching, but not by making every reader referee it.) */}
          <span className="type-caption text-muted">
            {anyFilter && 'Filtered: '}
            {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            {anyFilter && rows.length > 0 && (
              <>
                {' · '}<span className={IN_TEXT}>+{inr(totals.in)}</span> in
                {' · '}<span className={OUT_TEXT}>−{inr(totals.out)}</span> out
              </>
            )}
            {capped && <span className="ml-1 text-warning-600 dark:text-harvest-300">· latest {LEDGER_CAP} only — narrow the filters or export</span>}
          </span>
          <ExportLedgerButton
            repId={repId}
            repName={repName}
            params={params}
            scope={(
              <div className="space-y-1">
                <ScopeRow label="Entries">
                  {rows.length.toLocaleString('en-IN')} {rows.length === 1 ? 'entry' : 'entries'} — what the ledger below is showing
                  {capped && <span className="text-muted"> (the file is not capped at {LEDGER_CAP})</span>}
                </ScopeRow>
                <ScopeRow label="Period">
                  {from && to ? `${from.format('DD MMM YYYY')} – ${to.format('DD MMM YYYY')}`
                    : from ? `From ${from.format('DD MMM YYYY')}`
                      : to ? `Up to ${to.format('DD MMM YYYY')}` : 'All time'}
                </ScopeRow>
                <ScopeRow label="Filters">
                  {[
                    type && typeLabel(type),
                    direction === 'IN' ? 'Money in only' : direction === 'OUT' ? 'Money out only' : null,
                    verification && VERIFY_FILTER_LABEL[verification],
                    search.trim() && `Matching "${search.trim()}"`,
                  ].filter(Boolean).join(' · ') || 'None'}
                </ScopeRow>
                <ScopeRow label="Order">
                  {sortState ? `Date, ${sortState.dir === 'asc' ? 'ascending' : 'descending'}` : 'Newest entry first'}
                </ScopeRow>
              </div>
            )}
          />
        </div>
      </div>

      <DataTable
        storageKey="wallet-ledger"
        rowKey="id"
        dataSource={rows}
        loading={isFetching && !fullLedger}
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        pagination={{ pageSize: 15, showSizeChanger: true }}
        emptyText={<EmptyState title={anyFilter ? 'No entries match these filters' : 'No transactions yet'} />}
        /* Four controls, so they sit INLINE, each sized to what it holds. A filter PANEL is what the
           component itself prescribes past about four — below that it costs a sheet of mostly empty
           space (one group laid into a three-column grid, every control stretched to a shared cell
           width) to hide two dropdowns, and it hides which filters exist until you open it. */
        toolbarLeft={
          <>
            <Input
              prefix={<Search size={16} />} placeholder="Txn no or note" aria-label="Txn no or note" className="w-[150px]" allowClear
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            />
            <Select placeholder="Any type" allowClear className="w-[140px]"
              value={type} onChange={(v) => setType(v as string | undefined)}
              options={Object.keys(TYPE_LABEL).map((t) => ({ value: t, label: TYPE_LABEL[t] }))} />
            <Select placeholder="Any check" allowClear className="w-[124px]"
              value={verification} onChange={(v) => setVerification(v as string | undefined)}
              options={Object.keys(VERIFY_FILTER_LABEL).map((v) => ({ value: v, label: VERIFY_FILTER_LABEL[v] }))} />
            {/* Two independent dates, not one range control: "since the 1st" is a complete question,
                and a range field would refuse to answer it until an end date was invented. */}
            <DatePicker placeholder="From" allowClear size="small" disabledDate={disableFuture}
              value={from} maxDate={to ?? undefined} onChange={(d: any) => setFrom(d ?? null)} />
            <DatePicker placeholder="To" allowClear size="small" disabledDate={disableFuture}
              value={to} minDate={from ?? undefined} onChange={(d: any) => setTo(d ?? null)} />
            {anyFilter && (
              <FilterResetButton size="md" onClick={clearFilters} />
            )}
          </>
        }
      />
      {editRow && <EditTxnModal repId={repId} row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); inval(); }} />}
      {correctionRow && (
        <CorrectionModal
          row={correctionRow.row}
          req={correctionRow.req}
          onClose={() => setCorrectionRow(null)}
          onDone={() => { setCorrectionRow(null); inval(); }}
        />
      )}
      {verifyRow && (
        <VerifyTxnModal
          row={verifyRow}
          busy={verify.isPending}
          onClose={() => setVerifyRow(null)}
          onVerify={() => verify.mutate(verifyRow.id, { onSuccess: () => setVerifyRow(null) })}
        />
      )}
    </>
  );
}

/**
 * Review a rep's self-declared cash entry before verifying it.
 *
 * THE WORKFLOW THIS BELONGS TO: a Sales Person / Sales Head spends from the float and logs it in the
 * app — amount, what it was for, and a photo of the receipt, which the server REQUIRES. The money
 * moves immediately (their balance is theirs to spend) and the entry is parked as UNVERIFIED. An
 * approver then checks it against the receipt and marks it verified. That is the only thing the tick
 * in the ledger does.
 *
 * It used to be a "Mark this transaction verified?" confirm — asking someone to sign off on a row
 * whose amount, note and receipt were all somewhere else on the screen or nowhere at all. Approval
 * without the evidence in front of you is not approval; it is a click. So this shows the whole entry,
 * with the proof, and verifies from there.
 */
function VerifyTxnModal({ row, busy, onClose, onVerify }: { row: any; busy?: boolean; onClose: () => void; onVerify: () => void }) {
  const out = row.direction === 'OUT';
  const F = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="min-w-0">
      <div className="type-label text-muted">{label}</div>
      <div className="mt-0.5 type-body-medium text-content">{children ?? <span className="text-subtle">—</span>}</div>
    </div>
  );
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={560}
      title={<span>Review {row.txnNo}</span>}
      description="Declared by the rep. Check it against the receipt before verifying."
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={onVerify}>Verify</Button>
        </>
      }>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        <div>
          <div className="type-label text-muted">{typeLabel(row.type)}</div>
          <div className={`type-metric tabular-nums ${out ? OUT_TEXT : IN_TEXT}`}>
            {out ? '−' : '+'}{inr(row.amount)}
          </div>
        </div>
        <F label="Date">{formatIstDate(row.txnDate)}</F>
        <F label="Balance after">{inr(row.balanceAfter)}</F>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {/* On a rep's own entry this is how THEY paid a vendor out of the float they hold — no
            company account is involved, so it is named for what it is. (On a company payout the same
            field means the rail our money left by, and that one does name an account.) */}
        <F label="How the rep paid">{[modeLabel(row.paymentMode), row.paymentReference].filter(Boolean).join(' · ') || undefined}</F>
        <F label="Logged by">{row.postedByRole ? (ROLE_TAG[row.postedByRole]?.label ?? row.postedByRole) : undefined}</F>
        <div className="col-span-2"><F label="What it was for">{row.notes || row.reason || undefined}</F></div>
        <div className="col-span-2">
          <div className="type-label text-muted">Receipt</div>
          {/* The evidence itself, opened in place. A missing one is stated plainly rather than left
              as an empty space the approver has to interpret. */}
          <div className="mt-1">
            {row.attachmentUrl
              ? <AttachmentCell files={[row.attachmentUrl]} label="Receipt" size={88} />
              : <span className="type-body text-warning-600 dark:text-harvest-300">No receipt on this entry</span>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const CORRECTION_LABEL: Record<string, string> = {
  PENDING: 'Awaiting Super Admin', APPROVED: 'Approved', REJECTED: 'Rejected', APPLIED: 'Applied', CANCELLED: 'Cancelled',
};
const CORRECTION_TONE: Record<string, TagTone> = {
  PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', APPLIED: 'blue', CANCELLED: 'neutral',
};

/** Human wording for the fields a correction may change, so the diff reads as English. */
const CORRECTION_FIELD: Record<string, string> = {
  amount: 'Amount', direction: 'Direction', txnDate: 'Date', paymentMode: 'Payment mode',
  paymentReference: 'Reference', bankAccountId: 'Bank account', notes: 'Notes',
};

/**
 * A proposed correction: what would change, who asked, and the decision.
 *
 * The same dialog serves both halves of the maker-checker, because they are the same question asked
 * of different people: a SUPER ADMIN sees Approve / Reject, and once approved whoever holds the
 * accounts permission sees Apply. Nobody sees a button their role cannot use, and the diff is stated
 * field by field — before on the left, proposed on the right — so a decision is never taken on a
 * summary of itself.
 */
function CorrectionModal({ row, req, onClose, onDone }: { row: any; req: any; onClose: () => void; onDone: () => void }) {
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const canApply = usePermission('EXPENSE.APPROVE.ACCT');
  const [note, setNote] = useState('');

  const decide = useConfirmedMutation((approve) => ({ title: approve ? "Approve this correction request?" : "Reject this correction request?", content: approve ? "It can then be applied to the rep’s wallet." : "The wallet is left as it is and your note goes back to whoever raised it.", okText: approve ? "Yes, approve" : "Yes, reject", danger: !approve }), {
    mutationFn: (approve: boolean) => request({ url: `/wallets/corrections/${req.id}/decide`, method: 'POST', data: { approve, note: note || undefined } }),
    onSuccess: (_r: any, approve: boolean) => { message.success(approve ? 'Correction approved' : 'Correction rejected'); onDone(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const apply = useConfirmedMutation({ title: "Apply this correction to the wallet?", content: "The rep’s balance changes immediately. Undoing it needs another correction.", okText: "Yes, apply", danger: true }, {
    mutationFn: () => request({ url: `/wallets/corrections/${req.id}/apply`, method: 'POST' }),
    onSuccess: () => { message.success('Correction applied'); onDone(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const fmt = (k: string, v: any) => {
    if (v === null || v === undefined || v === '') return '—';
    if (k === 'amount') return inr(v);
    if (k === 'txnDate') return formatIstDate(v);
    if (k === 'paymentMode') return modeLabel(v);
    return String(v);
  };
  const changed = Object.keys(req.proposed ?? {}).filter(
    (k) => String((req.snapshot ?? {})[k] ?? '') !== String((req.proposed ?? {})[k] ?? ''),
  );

  const pending = req.status === 'PENDING';
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={620}
      title={<span>Correction on {req.txnNo} <Tag tone={CORRECTION_TONE[req.status] ?? 'neutral'}>{CORRECTION_LABEL[req.status] ?? req.status}</Tag></span>}
      description={pending
        ? 'Proposed by the person who spotted it. Approving does not change anything by itself — the approved values are applied afterwards.'
        : req.status === 'APPROVED'
          ? 'Approved. Applying writes exactly these values and recomputes the running balance.'
          : 'Closed. Kept as the record of what was asked and what was decided.'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Close</Button>
          {pending && isSuperAdmin && (
            <>
              <Button variant="soft-danger" loading={decide.isPending} onClick={() => decide.mutate(false)}>Reject</Button>
              <Button variant="primary" loading={decide.isPending} onClick={() => decide.mutate(true)}>Approve</Button>
            </>
          )}
          {req.status === 'APPROVED' && canApply && (
            <Button variant="primary" loading={apply.isPending} onClick={() => apply.mutate()}>Apply the correction</Button>
          )}
        </>
      }>
      <div className="mb-4 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        <div className="type-label text-muted">Why</div>
        <div className="type-body text-content">{req.reason}</div>
        <div className="mt-2 flex items-center gap-2">
          <span className="type-label text-muted">Evidence</span>
          <span onClick={(e) => e.stopPropagation()}>
            <AttachmentCell files={req.attachmentUrl ? [req.attachmentUrl] : []} label="Evidence" size={40}
              empty={<span className="type-caption text-subtle">none attached (raised before evidence was required)</span>} />
          </span>
        </div>
      </div>

      {/* WHO did WHAT and WHEN. All three stamps are recorded on every correction; showing them is
          what turns an audit table into an audit trail somebody can actually read. */}
      <div className="mb-4">
        <div className="type-label text-muted">Timeline</div>
        <ol className="mt-1 space-y-1">
          {[
            { label: 'Raised', who: req.requestedByName ?? `#${req.requestedBy}`, at: req.requestedAt },
            ...(req.decidedAt ? [{ label: req.status === 'REJECTED' ? 'Rejected' : 'Approved', who: req.decidedByName ?? `#${req.decidedBy}`, at: req.decidedAt, note: req.decisionNote }] : []),
            ...(req.appliedAt ? [{ label: 'Applied', who: req.appliedByName ?? `#${req.appliedBy}`, at: req.appliedAt }] : []),
          ].map((st: any) => (
            <li key={st.label} className="flex flex-wrap items-baseline gap-x-2 type-body">
              <span className="w-20 shrink-0 type-caption text-muted">{st.label}</span>
              <span className="text-content">{st.who}</span>
              <span className="type-caption text-subtle">{formatIst(st.at)}</span>
              {st.note && <span className="type-caption text-subtle">· {st.note}</span>}
            </li>
          ))}
          {req.status === 'APPROVED' && !req.appliedAt && (
            <li className="flex items-baseline gap-x-2 type-body">
              <span className="w-20 shrink-0 type-caption text-muted">Applied</span>
              <span className="type-caption text-subtle">not yet</span>
            </li>
          )}
        </ol>
      </div>

      <div className="type-label text-muted">What would change</div>
      <div className="mt-1 overflow-hidden rounded-card border border-line">
        {changed.length === 0 && <div className="px-3 py-2 type-body text-subtle">Nothing differs from the current entry.</div>}
        {changed.map((k) => (
          <div key={k} className="flex items-center gap-2 border-b border-line px-3 py-2 last:border-b-0">
            <span className="w-32 shrink-0 type-caption text-muted">{CORRECTION_FIELD[k] ?? k}</span>
            <span className="min-w-0 flex-1 truncate type-body text-subtle line-through">{fmt(k, (req.snapshot ?? {})[k])}</span>
            <ArrowRight className="size-3.5 shrink-0 text-subtle" />
            <span className="min-w-0 flex-1 truncate type-body-medium text-content">{fmt(k, (req.proposed ?? {})[k])}</span>
          </div>
        ))}
      </div>

      {pending && isSuperAdmin && (
        <div className="mt-4">
          {fieldLabel('Note (optional)')}
          <Input placeholder="Recorded with the decision" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}
      {pending && !isSuperAdmin && (
        <div className="mt-4 type-caption text-subtle">Only a Super Admin can decide this. It stays here until they do.</div>
      )}
    </Modal>
  );
}

/** Field label for the wallet dialogs — 12px muted, with a red asterisk where the field is required. */
const fieldLabel = (text: string, required?: boolean) => (
  <div className="mb-1 type-label text-muted">{text}{required && <span className="text-danger-500"> *</span>}</div>
);

// ── Admin: edit a manually-posted ledger entry (recomputes the wallet) ────────
function EditTxnModal({ repId, row, onClose, onSaved }: { repId: string; row: any; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState<number | undefined>(Number(row.amount));
  const [direction, setDirection] = useState<'IN' | 'OUT'>(row.direction);
  const [txnDate, setTxnDate] = useState<Dayjs | null>(row.txnDate ? dayjs(row.txnDate) : null);
  const [paymentMode, setPaymentMode] = useState<string | undefined>(row.paymentMode ?? undefined);
  const [paymentReference, setPaymentReference] = useState(row.paymentReference ?? '');
  const [notes, setNotes] = useState(row.notes ?? '');
  const isAdjustment = row.type === 'ADJUSTMENT' || row.type === 'OPENING_BALANCE';
  /* A non-cash movement must name the company account it went through — the same rule a fresh
     posting obeys, now enforced on edits too. Without this picker the form could set a mode it was
     then unable to satisfy, so switching CASH → UPI would fail with no way to fix it. ADJUSTMENT and
     the expense pair move no bank money of their own and stay exempt. */
  const [bankAccountId, setBankAccountId] = useState<string | undefined>(row.bankAccountId ? String(row.bankAccountId) : undefined);
  const bankExempt = ['ADJUSTMENT', 'EXPENSE_OUT', 'EXPENSE_REVERSAL'].includes(row.type);
  const needsBank = !bankExempt && !!paymentMode && paymentMode !== 'CASH';
  const { data: bankAccounts } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
    enabled: !bankExempt,
  });

  /* A posted entry is not edited here any more. This form PROPOSES the new values; a Super Admin
     approves or rejects; the approved values are applied afterwards from the same row. The reason is
     required because the approver is deciding on the reason, not on the numbers alone. */
  const [reason, setReason] = useState('');
  /* The approver was not there when the entry was posted, so the thing that PROVES it is wrong -
     the voucher, the bank line, the photo - travels with the request. The server refuses it without
     one; the button says so first. */
  const [evidence, setEvidence] = useState<string | undefined>();
  /* Nothing to approve if nothing differs. The form opens pre-filled with the entry's current
     values, so without this a reason alone could be sent up for a decision on a change that was
     never made — the approver would open it and read "nothing differs from the current entry". */
  const hasChange = (
    Number(amount) !== Number(row.amount)
    || (txnDate ? txnDate.format('YYYY-MM-DD') : '') !== String(row.txnDate ?? '').slice(0, 10)
    || (paymentMode ?? '') !== (row.paymentMode ?? '')
    || (paymentReference ?? '') !== (row.paymentReference ?? '')
    || (notes ?? '') !== (row.notes ?? '')
    || (isAdjustment && direction !== row.direction)
    || (!bankExempt && String(bankAccountId ?? '') !== String(row.bankAccountId ?? ''))
  );
  const save = useConfirmedMutation({ title: "Send this correction for approval?", content: "Nothing on the wallet changes until it is approved and applied.", okText: "Yes, send it" }, {
    mutationFn: () => request({
      url: `/wallets/${repId}/transactions/${row.id}/corrections`, method: 'POST',
      data: {
        reason, attachmentUrl: evidence,
        amount, txnDate: txnDate ? txnDate.format('YYYY-MM-DD') : undefined,
        paymentMode: paymentMode || undefined, paymentReference: paymentReference || undefined,
        notes: notes || undefined, ...(isAdjustment ? { direction } : {}),
        ...(bankExempt ? {} : { bankAccountId: needsBank ? bankAccountId : null }),
      },
    }),
    onSuccess: () => { message.success('Correction sent for Super Admin approval'); onSaved(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not raise the correction'),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={560}
      title={`Propose a correction to ${row.txnNo}`}
      description="A posted entry cannot be changed directly. This goes to a Super Admin, and the approved values are applied afterwards."
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0) || (needsBank && !bankAccountId) || !reason.trim() || !hasChange || !evidence} onClick={() => save.mutate()}>Send for approval</Button></>}>
      {/* What is being edited, stated once: the entry's kind and where it currently stands. The type
          used to be a Tag on its own line, which stretched the full width of the dialog into a
          coloured bar shouting "ADJUSTMENT" — a chip is a label, not a banner. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-card border border-line bg-surface-2/50 px-4 py-3">
        <Tag tone={TXN_TYPE_COLOR[row.type]} style={{ marginInlineEnd: 0 }}>{typeLabel(row.type)}</Tag>
        <span className="type-caption text-muted">
          posted <span className="text-content">{formatIstDate(row.txnDate)}</span>
          {' · '}currently <span className={`tabular-nums ${row.direction === 'OUT' ? OUT_TEXT : IN_TEXT}`}>
            {row.direction === 'OUT' ? '−' : '+'}{inr(row.amount)}
          </span>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <div>
          {fieldLabel('Amount ₹', true)}
          <InputNumber min={1} className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        </div>
        <div>
          {fieldLabel('Date')}
          <DatePicker className="w-full" value={txnDate} onChange={setTxnDate} />
        </div>
        {isAdjustment && (
          <div>
            {fieldLabel('Direction')}
            <Select className="w-full" value={direction} onChange={(v) => setDirection(v as 'IN' | 'OUT')}
              options={[{ value: 'IN', label: 'Into the wallet (IN)' }, { value: 'OUT', label: 'Out of the wallet (OUT)' }]} />
          </div>
        )}
        {/* A closed set, not free text — the server records only these, and the cash-vs-bank rule keys off the value. */}
        <div>
          {fieldLabel('Payment mode')}
          <Select className="w-full" allowClear placeholder="Not recorded" value={paymentMode} onChange={(v: any) => setPaymentMode(v || undefined)}
            options={PAYMENT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
        </div>
        {needsBank && (
          <div className={isAdjustment ? '' : 'sm:col-span-2'}>
            {fieldLabel('From which bank account', true)}
            <Select className="w-full" placeholder="Company bank account" value={bankAccountId} onChange={(v: any) => setBankAccountId(v || undefined)}
              options={(bankAccounts ?? []).map((a: any) => ({
                value: String(a.id),
                label: `${a.label || a.bankName}${a.last4 ? ` · ····${a.last4}` : ''}`,
              }))}
              notFoundContent="No active company bank account — add or reactivate one in Company & Billing setup" />
          </div>
        )}
        <div className="sm:col-span-2">
          {fieldLabel('Reference')}
          <Input className="w-full" placeholder="UTR, cheque or voucher no" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          {fieldLabel('Notes')}
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          {fieldLabel('Why is this wrong?', true)}
          <Textarea rows={2} placeholder="What went wrong, and how you know" value={reason} onChange={(e) => setReason(e.target.value)} />
          {!hasChange && <div className="mt-1 type-caption text-subtle">Change a value above — there is nothing to approve yet.</div>}
        </div>
        <div className="sm:col-span-2">
          {fieldLabel('Evidence', true)}
          <FileUploadField category="receipt" buttonLabel={evidence ? 'Replace evidence' : 'Attach the voucher, statement or photo'}
            value={evidence} onChange={setEvidence} />
        </div>
      </div>
    </Modal>
  );
}

// ── Admin: record a wallet transaction ───────────────────────────────────────
function RecordTxnModal({ repId, onClose }: { repId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<typeof TXN_TYPES[number]>('ADVANCE_IN');
  const [amount, setAmount] = useState<number | undefined>();
  const [direction, setDirection] = useState<'IN' | 'OUT'>('IN');
  const [paymentMode, setPaymentMode] = useState<string | undefined>();
  const [paymentReference, setPaymentReference] = useState('');
  const [txnDate, setTxnDate] = useState<Dayjs | null>(dayjs());
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();
  // Which company account a non-cash movement went through — the ledger requires it.
  const [bankAccountId, setBankAccountId] = useState<string | undefined>();
  const { data: bankRaw } = useQuery({
    queryKey: ['bank-account-options'],
    queryFn: () => request<any[]>({ url: '/company/bank-accounts/options' }),
  });
  const bankOpts = (Array.isArray(bankRaw) ? bankRaw : ((bankRaw as any)?.data ?? []));

  const needsDirection = type === 'ADJUSTMENT' || type === 'OPENING_BALANCE';
  const needsReason = type === 'ADJUSTMENT';

  const save = useConfirmedMutation({ title: "Record this wallet transaction?", content: "It posts to the rep’s wallet straight away.", okText: "Yes, record" }, {
    mutationFn: () => request({
      url: `/wallets/${repId}/transactions`, method: 'POST',
      data: {
        type,
        bankAccountId: bankAccountId ?? null,
        amount: Number(amount),
        direction: needsDirection ? direction : undefined,
        paymentMode: paymentMode || undefined,
        paymentReference: paymentReference.trim() || undefined,
        txnDate: txnDate ? txnDate.format('YYYY-MM-DD') : undefined,
        reason: needsReason ? reason.trim() : undefined,
        notes: notes.trim() || undefined,
        attachmentUrl: attachmentUrl || undefined,
      },
    }),
    onSuccess: () => {
      message.success('Transaction recorded');
      qc.invalidateQueries({ queryKey: ['wallet', repId] });
      qc.invalidateQueries({ queryKey: ['wallet-stats'] });
      qc.invalidateQueries({ queryKey: ['wallets'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // A non-cash ADVANCE_IN must carry its receipt (the ledger refuses one without it).
  const needsProof = type === 'ADVANCE_IN' && !!paymentMode && paymentMode !== 'CASH';
  /*
   * ...and ANY non-cash wallet movement must name the company account it went through — the ledger
   * refuses one without it (BANK_ACCOUNT_REQUIRED), exactly as a customer receipt does.
   *
   * This form had no such field, so picking UPI / bank transfer / cheque here was a DEAD END: the
   * post came back 400 with nothing on screen able to satisfy it, and the only way through was to
   * record the payout as CASH. That is how a bank-paid reimbursement ends up reducing CASH IN HAND
   * and never appears in the register of the account it actually left.
   */
  const needsBank = !!paymentMode && paymentMode !== 'CASH' && type !== 'ADJUSTMENT';
  const disabled = !(Number(amount) > 0) || (needsReason && !reason.trim())
    || (needsProof && !attachmentUrl) || (needsBank && !bankAccountId);

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Record transaction"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={disabled} onClick={() => save.mutate()}>Record</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Type</div>
          <Select className="w-full" value={type} onChange={(v) => setType(v as typeof TXN_TYPES[number])}
            options={TXN_TYPES.map((t) => ({ value: t, label: t }))} />
        </div>
        {needsDirection && (
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Direction</div>
            <Select className="w-full" value={direction} onChange={(v) => setDirection(v as 'IN' | 'OUT')}
              options={[{ value: 'IN', label: 'IN (credit to rep)' }, { value: 'OUT', label: 'OUT (debit from rep)' }]} />
          </div>
        )}
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Amount</div>
          <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Payment mode</div>
          <Select className="w-full" placeholder="Payment mode (optional)" value={paymentMode} onChange={setPaymentMode}
            options={PAYMENT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
        </div>
        {needsBank && (
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Company bank account *</div>
            <Select className="w-full" showSearch optionFilterProp="searchLabel"
              placeholder={bankOpts.length ? 'Which account did the money move through?' : 'No bank accounts configured'}
              disabled={!bankOpts.length}
              value={bankAccountId} onChange={(v) => setBankAccountId(v as string)}
              options={bankOpts.map(bankAcctOption)} />
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              Required for every non-cash movement — it is what puts this entry in that account&apos;s register.
            </div>
          </div>
        )}
        <Input placeholder="Payment reference / UTR (optional)" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Txn date</div>
          <DatePicker className="w-full" value={txnDate} onChange={setTxnDate} />
        </div>
        {needsReason && (
          <Textarea rows={2} placeholder="Reason (required for adjustment)" value={reason} onChange={(e) => setReason(e.target.value)} />
        )}
        <Textarea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        {/* A non-cash advance must carry its receipt — the server refuses one without it, so the
            button says so first rather than letting the post fail after the form is filled in. */}
        <FileUploadField
          category="receipt"
          buttonLabel={needsProof ? 'Upload payment proof (required)' : 'Upload proof (optional)'}
          value={attachmentUrl} onChange={setAttachmentUrl}
        />
      </Space>
    </Modal>
  );
}

// ── Rep view: their own wallet ───────────────────────────────────────────────
function MyWalletView() {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const myId = useAuthStore((s) => s.user?.id);
  const badges = useBadges();
  // Backend requires BOTH the permission and a field-staff role; don't show a button that 403s.
  // Both hooks are called unconditionally — `&&` between them would skip one and break hook order.
  const hasAdvancePerm = usePermission('ADVANCE.REQUEST');
  const isFieldStaff = useHasRole('SALES_PERSON', 'SALES_HEAD');
  const canRequest = hasAdvancePerm && isFieldStaff;
  const { data: wallet, isLoading } = useQuery({
    queryKey: ['wallet', 'me'],
    queryFn: () => request<any>({ url: '/wallets/me' }),
  });
  /* A decision on one of the rep's own advances links to `/wallets?tab=mine&focus=<id>`: open Top-up activity, where
     the request is found and opened. Followed on change too — a tap in the bell does not remount this view. */
  const [sp] = useSearchParams();
  const focusId = sp.get('focus');
  const [activityTab, setActivityTab] = useState<string>(focusId ? 'topups' : 'expenses');
  useEffect(() => { if (focusId) setActivityTab('topups'); }, [focusId]);

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} align="center" wrap>
        <Typography.Title level={4} style={{ margin: 0 }}>My wallet</Typography.Title>
        <Space wrap>
          {canRequest && <Button variant="primary" icon={<Plus size={16} />} onClick={() => setTopUpOpen(true)}>Request top-up</Button>}
        </Space>
      </Space>

      <Card>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : !wallet ? (
          <EmptyState title="No wallet found" />
        ) : (
          /* The rep sees their own wallet in full - the same four figures and the same ledger an
             admin sees - but read-only: `canVerify={false}` withholds verify, correct and reverse,
             which are somebody else's job. Their own entries are what they are being asked to
             account for, so hiding them served nobody. */
          <WalletBody wallet={wallet} repId={String(wallet.repId)} canVerify={false} />
        )}
      </Card>

      {myId && (
        <Card className="mt-4">
          <Tabs
            activeKey={activityTab}
            onChange={setActivityTab}
            items={[
              { key: 'expenses', label: 'My expenses', children: <MyExpensesTab repId={String(myId)} /> },
              {
                key: 'topups',
                label: (
                  <Space size={6}>
                    Top-up activity
                    {!!badges.myAdvanceRequestsPending && (
                      <Tag tone="gold">{badges.myAdvanceRequestsPending}</Tag>
                    )}
                  </Space>
                ),
                children: <MyTopUpsTab />,
              },
            ]}
          />
        </Card>
      )}

      {topUpOpen && <TopUpModal onClose={() => setTopUpOpen(false)} />}
    </div>
  );
}

// Top-up (advance) activity — the logged-in sales person's own requests across every status,
// so they can track REQUESTED → APPROVED → DISBURSED (or REJECTED) with full detail.
function MyTopUpsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['advance-requests', 'mine'],
    queryFn: () => request<any[]>({ url: '/advance-requests' }),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'requestNo', title: 'Request No', dataIndex: 'requestNo', alwaysVisible: true, sortable: true },
    { key: 'amount', title: 'Amount', dataIndex: 'amount', align: 'right', sortable: true, render: (v: any) => <Money value={inr(v)} strong /> },
    { key: 'purpose', title: 'Purpose', dataIndex: 'purpose', ellipsis: true, render: (v: any) => v || '—' },
    {
      // "Awaiting payment", not "APPROVED": to the rep who asked, the useful fact is what happens
      // next, and a request can be approved and still unpaid.
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (s: string) => <Tag tone={ADV_STATUS_COLOR[s] ?? 'neutral'} className="whitespace-nowrap">{ADV_STATUS_LABEL[s] ?? s}</Tag>,
    },
    { key: 'createdAt', title: 'Requested', dataIndex: 'createdAt', sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', render: (v: any, r: any) => (v ? <Space size={4} wrap>{v}<RoleTag role={r.approvedByRole} />{r.approvedAt && <span style={{ color: '#888', fontSize: 12 }}>{formatIstDate(r.approvedAt)}</span>}</Space> : '—') },
    {
      key: 'disbursed', title: 'Disbursed', dataIndex: 'disbursedAt',
      render: (_: any, r: any) => r.status === 'DISBURSED'
        ? <Space size={4} wrap><span style={{ color: 'var(--color-brand-600)' }}>{formatIstDate(r.disbursedAt)}</span>{r.paymentMode && <Tag>{r.paymentMode}{r.paymentReference ? ` · ${r.paymentReference}` : ''}</Tag>}</Space>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      /* What they attached when they asked — their own evidence, back in front of them. */
      key: 'requestProofUrl', title: 'Document', dataIndex: 'requestProofUrl', align: 'center', width: 92,
      render: (v: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={v ? [v] : []} label="Supporting document" size={34} />
        </span>
      ),
    },
    {
      key: 'paymentProofUrl', title: 'Pay receipt', dataIndex: 'paymentProofUrl', align: 'center', width: 100,
      render: (v: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <AttachmentCell files={v ? [v] : []} label="Payment proof" size={34} />
        </span>
      ),
    },
    { key: 'rejectReason', title: 'Note', dataIndex: 'rejectReason', ellipsis: true, render: (v: any, r: any) => r.status === 'REJECTED' ? <span style={{ color: 'var(--color-danger-500)' }}>{v || 'Rejected'}</span> : '—' },
  ];

  const [topUpDetail, setTopUpDetail] = useState<any>(null);
  // `?focus=<id>` from an "Advance approved / rejected / paid out" notice: tint the request and open it.
  const focus = useFocusRow(data, { match: ['id', 'requestNo'], onFocus: (r: any) => setTopUpDetail(r) });

  return (
    <>
    <DataTable
      storageKey="my-topups"
      rowKey="id"
      loading={isLoading}
      dataSource={data ?? []}
      columns={columns}
      rowClassName={focus.rowClassName}
      pagination={{ pageSize: 10, showSizeChanger: true }}
      onRow={(r: any) => ({ onClick: () => setTopUpDetail(r), style: { cursor: 'pointer' } })}
      emptyText={<EmptyState title="No top-up requests yet" />}
    />
      {topUpDetail && <RequestDetailModal req={topUpDetail} onClose={() => setTopUpDetail(null)} />}
    </>
  );
}

// My expenses — every expense report filed by / for the logged-in user. Clicking a row opens
// a modal with that report's full wallet ledger trail (EXPENSE_OUT / reversal / adjustment).
function MyExpensesTab({ repId }: { repId: string }) {
  const [openReport, setOpenReport] = useState<any | null>(null);
  const { data: reports, isLoading } = useQuery({
    queryKey: ['my-expense-reports', repId],
    queryFn: () => request<any[]>({ url: '/expense-reports', params: { repId } }),
  });
  // All wallet rows tied to an expense report, fetched once and grouped by report id.
  const { data: ledger } = useQuery({
    queryKey: ['my-expense-ledger', repId],
    queryFn: () => request<any[]>({ url: `/wallets/${repId}/transactions`, params: { referenceType: 'EXPENSE_REPORT' } }),
  });
  const ledgerByReport = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const t of ledger ?? []) {
      const k = String(t.referenceId);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return m;
  }, [ledger]);

  const columns: DataTableColumn<any>[] = [
    { key: 'reportNo', title: 'Report No', dataIndex: 'reportNo', alwaysVisible: true, sortable: true },
    {
      key: 'partyName', title: 'Party / Visit', dataIndex: 'partyName',
      render: (v: any, r: any) => (
        <Space size={4} wrap>{v ?? <span style={{ color: '#ccc' }}>—</span>}{r.visitType && <Tag>{String(r.visitType).replace(/_/g, ' ')}</Tag>}</Space>
      ),
    },
    { key: 'subtotalAmount', title: 'Amount', dataIndex: 'subtotalAmount', align: 'right', sortable: true, render: (v: any) => <Money value={inr(v)} strong /> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, render: (s: string) => <Tag tone={REPORT_STATUS_COLOR[s] ?? 'neutral'}>{(s ?? '').replace(/_/g, ' ')}</Tag> },
    { key: 'filed', title: 'Filed', dataIndex: 'createdAt', sortable: true, render: (v: string, r: any) => formatIstDate(r.submittedAt ?? v) },
    {
      key: 'ledger', title: 'Ledger', align: 'center', width: 90,
      render: (_: any, r: any) => {
        const n = ledgerByReport.get(String(r.id))?.length ?? 0;
        return n ? <Tag tone="blue">{n} txn</Tag> : <span style={{ color: '#ccc' }}>—</span>;
      },
    },
  ];

  return (
    <>
      <DataTable
        storageKey="my-expenses"
        rowKey="id"
        loading={isLoading}
        dataSource={reports ?? []}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        onRow={(r: any) => ({ onClick: () => setOpenReport(r), style: { cursor: 'pointer' } })}
        emptyText={<EmptyState title="No expenses yet" />}
      />
      {openReport && (
        <ExpenseLedgerModal
          report={openReport}
          ledger={ledgerByReport.get(String(openReport.id)) ?? []}
          onClose={() => setOpenReport(null)}
        />
      )}
    </>
  );
}

// One expense's money trail — the report summary + every wallet ledger row linked to it.
function ExpenseLedgerModal({ report, ledger, onClose }: { report: any; ledger: any[]; onClose: () => void }) {
  const navigate = useNavigate();
  const columns = [
    { title: 'Txn No', dataIndex: 'txnNo' },
    { title: 'Date', dataIndex: 'txnDate', render: (v: string) => formatIstDate(v) },
    { title: 'Type', dataIndex: 'type', render: (v: string) => <Tag tone={TXN_TYPE_COLOR[v]}>{typeLabel(v)}</Tag> },
    { title: 'Dir', dataIndex: 'direction', render: (v: string) => <Tag tone={v === 'IN' ? 'green' : 'red'}>{v}</Tag> },
    {
      title: 'Amount', dataIndex: 'amount', align: 'right' as const,
      render: (v: any, r: any) => <Money value={`${r.direction === 'IN' ? '+' : '−'}${inr(v)}`} color={r.direction === 'IN' ? PALETTE.brand[600] : PALETTE.danger[500]} strong />,
    },
    { title: 'Balance after', dataIndex: 'balanceAfter', align: 'right' as const, render: (v: any) => <span style={{ color: balanceColor(v) }}>{inr(v)}</span> },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v: any, r: any) => v ?? r.reason ?? '—' },
  ];

  return (
    <Modal
      open width={820} onOpenChange={(o) => { if (!o) onClose(); }}
      title={<Space wrap>Expense {report.reportNo}<Tag tone={REPORT_STATUS_COLOR[report.status] ?? 'neutral'}>{(report.status ?? '').replace(/_/g, ' ')}</Tag></Space>}
      footer={[
        <Button key="close" variant="default" onClick={onClose}>Close</Button>,
        <Button key="open" variant="primary" onClick={() => navigate(`/expense-reports/${report.id}`)}>Open full report →</Button>,
      ]}
    >
      <Descriptions bordered size="small" column={2} className="mb-4">
        <Descriptions.Item label="Party">{report.partyName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Visit type">{report.visitType ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Amount">{inr2(report.subtotalAmount)}</Descriptions.Item>
        <Descriptions.Item label="Filed">{formatIst(report.createdAt)}</Descriptions.Item>
      </Descriptions>

      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Transaction ledger</Typography.Text>
      <Table
        rowKey="id" size="small" pagination={false} dataSource={ledger} columns={columns as any}
        locale={{ emptyText: <EmptyState title="No wallet transactions yet — not submitted." /> }}
      />
    </Modal>
  );
}

function TopUpModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | undefined>();
  const [purpose, setPurpose] = useState('');
  const [proof, setProof] = useState<string | undefined>();

  const save = useConfirmedMutation({ title: "Send this top-up request?", content: "It goes for approval before any money moves.", okText: "Yes, send it" }, {
    mutationFn: () => request({
      url: '/advance-requests/request', method: 'POST',
      data: { amount: Number(amount), purpose: purpose.trim(), requestProofUrl: proof },
    }),
    onSuccess: () => {
      message.success('Top-up requested');
      qc.invalidateQueries({ queryKey: ['advance-requests'] });
      qc.invalidateQueries({ queryKey: ['advance-request-stats'] });
      qc.invalidateQueries({ queryKey: ['badges'] }); // light up the pending badge immediately
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const label = (t: string, required?: boolean) => (
    <div className="mb-1 type-label text-muted">{t}{required && <span className="text-danger-500"> *</span>}</div>
  );

  /* This is the rep's OWN door to the same endpoint the register's request form uses, so it carries
     the same rule: no document, no request. Two forms posting to one endpoint must agree about what
     that endpoint requires, or one of them sends the rep into a server error they cannot act on. */
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Request top-up"
      description="Company money — say how much, what for, and attach what backs it up."
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!(Number(amount) > 0) || !purpose.trim() || !proof} onClick={() => save.mutate()}>Request</Button></>}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <div className="w-full">
          {label('Amount', true)}
          <InputNumber min={1} placeholder="Amount ₹" className="w-full" value={amount} onChange={(v) => setAmount(v == null ? undefined : Number(v))} />
        </div>
        <div className="w-full">
          {label('Purpose', true)}
          <Textarea rows={2} placeholder="Purpose (e.g. 3-day Pune-east tour)" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </div>
        <div className="w-full">
          {label('Supporting document', true)}
          <FileUploadField category="receipt" buttonLabel={proof ? 'Replace document' : 'Attach bill / quotation / screenshot'} value={proof} onChange={setProof} />
          <div className="mt-1 type-caption text-subtle">Required — whoever approves this sees it before releasing money.</div>
        </div>
      </Space>
    </Modal>
  );
}

// Compact, colour-coded KPI tile (greyed out when zero so live numbers stand out).
/** Delegates to the shared StatCard. The hand-rolled version painted its own background from a `bg`
 *  hex (`#E6F4FF`, `#FFF1F0`…) that stayed light in dark mode, making these the only tiles in the app
 *  with a light card on a dark canvas. `muted` (a zero value) now reads as the neutral tone rather
 *  than a separate grey palette. */
function KpiTile({ label, value, color, muted }: { label: string; value: string; color: string; bg?: string; muted?: boolean }) {
  return (
    <StatCard
      label={label}
      value={value}
      tone={muted ? 'neutral' : statTone(color)}
      // Width comes from the grid track (KPI_ROW), not from the tile — `min-w-0` so a long label
      // truncates inside its cell instead of forcing the column wider.
      className="min-w-0"
    />
  );
}
